// Tokyo, "Neon rain by Tokyo Tower" (spring). Tokyo Tower glows among high-rises whose red aviation
// lights blink in sync; the Skytree and Mt. Fuji stand far off; a commuter train crosses a brick
// viaduct; a yokocho alley is stacked with neon; a cherry tree blooms under a street lamp; the wet
// asphalt mirrors it all. Sakura petals and light rain fall at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, inEvent, cycle, hash, rng, bayer, hex, mix,
  makeCanvas, rect, makeHalo, drawHalo, streak, FONT3, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const GROUND = 214;                                   // far street line (feet of the viaduct arches)
const TWR = { cx: 246, base: 192, deck: 114, deckB: 121, top: 70, topB: 75, lat: 55, ant: 36 };
const SKYT = { x: 150, top: 47 };
const FUJI = { x: 338, y: 146 };
const MOON = { x: 198, y: 30 };
const AB = { r: 104, roof: 40, base: 244 };           // alley building: facade x 0..103, standing on y 244
const COL = { x: 94, w: 15 };                         // stacked vertical sign column
const SIG = { x: 114 };                               // pedestrian signal pole
const LAMP = { x: 393, y: 160, post: 402 };           // street lamp head (light centre) and post
const TRAIN_Y = 162;                                  // train sprite top (pantograph tips touch the wire)
const ZEB = { y0: 230, y1: 245, vx: 246, vy: 140, x0: 120, x1: 378 };
const BB = { x: 170, y: 132, w: 44, h: 16 };          // the trackside billboard's face (the site paints its pitch here)
const BB_POST = { x: BB.x + (BB.w >> 1) - 2, w: 4 };  // its steel column, rising from beyond the tracks
// three hidden things (site/js/ui/secrets.js, exported as `secrets`): a teru teru bozu, the paper doll hung up against
// rain, in a window of the alley building; a white-eye in the cherry tree; and a saucer that hovers a while between
// the Skytree and the tower
const BOZU = { x: 58, y: 150 };                       // its string's top, on the bottom right window's frame
const BIRD = { x: 418, y: 95 };                       // sprite origin, on a twig in the crown's dark heart
const UFO = { x: 176, y: 54 };                        // sprite origin
const UFO_UP = [[14, 28], [134, 148]];                // it shows: 1.5 s fading in, hovering, 1.5 s fading out

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
const rgbaOf = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/**
 * Local variant of core reflect(): same maths, but it snapshots the mirrored rows once into a scratch
 * buffer instead of reading the live canvas row by row (each self-read forces a full bitmap copy).
 */
let RBUF, RCTX;
function snapRows(top, span, x, w) {
  RCTX.globalCompositeOperation = 'copy';
  RCTX.drawImage(ctx.canvas, x, top - span, w, span, 0, 0, w, span);
  RCTX.globalCompositeOperation = 'source-over';
}
function reflectRows(t, { top, rows, squash = 1.6, alpha = 0.5, k1 = 45, k2 = 110, amp = 0.6, grow = 0.05, x = 0, w = W, puddles = [] }) {
  const span = Math.min(top, Math.floor((rows - 1) * Math.max(squash, 1.5)) + 1), y0 = top - span;
  snapRows(top, span, x, w);
  for (let k = 0; k < rows; k++) {
    const sy = top - 1 - Math.floor(k * squash);
    if (sy < 0) break;
    const off = Math.round(Math.sin(TAU * k1 * t / LOOP + k * 0.55) * (amp + k * grow) + Math.sin(TAU * k2 * t / LOOP - k * 1.3) * 0.7);
    ctx.globalAlpha = alpha * (1 - k / (rows + 20));
    ctx.drawImage(RBUF, 0, sy - y0, w, 1, x + off, top + k, w, 1);
  }
  // puddles: a crisper, brighter mirror from the same snapshot
  ctx.globalAlpha = 0.5;
  for (const [cx, cy, rx, ry] of puddles) for (let y = cy - ry; y <= cy + ry; y++) {
    const hw = Math.floor(rx * Math.sqrt(Math.max(0, 1 - ((y - cy) / ry) ** 2)));
    const k = y - top, sy = top - 1 - Math.floor(k * 1.5);
    if (hw < 1 || sy < y0) continue;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * 0.6);
    ctx.drawImage(RBUF, cx - hw - x, sy - y0, hw * 2, 1, cx - hw + off, y, hw * 2, 1);
  }
  ctx.globalAlpha = 1;
}
/** draw a glyph given as rows of '#'/'.' into a pix buffer */
function glyphP(p, g, x, y, col) { g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.set(x + i, y + j, col); }); }

/* ================= Invented kanji-like glyphs (no real words) ================= */
const G7 = [
  ['...#...', '#######', '...#...', '.#####.', '.#...#.', '.#####.', '#.....#'],
  ['#.#.#..', '#######', '#.....#', '#.###.#', '#.#.#.#', '#.###.#', '#....##'],
  ['..#.#..', '.#####.', '..#.#..', '#######', '..#.#..', '.#...#.', '#.....#'],
  ['#######', '#..#..#', '#######', '...#...', '#######', '...#...', '..###..'],
  ['.#..#..', '#####.#', '.#..#.#', '.##.#.#', '##..#.#', '.#..#..', '.#.##..'],
  ['...#...', '..#.#..', '.#...#.', '#.###.#', '...#...', '.#####.', '...#...'],
  ['#####..', '....#..', '.####..', '.#.....', '.######', '......#', '..####.'],
  ['.#...#.', '#######', '.#...#.', '.#####.', '.#...#.', '.#####.', '#.#.#.#'],
];
const G5 = [
  ['#####', '..#..', '#####', '#.#.#', '#####'], ['.#.#.', '#####', '.#.#.', '#####', '#...#'],
  ['#.###', '#..#.', '###.#', '#..#.', '#.###'], ['..#..', '#####', '.#.#.', '#...#', '.###.'],
  ['###.#', '#.#.#', '###.#', '#.#.#', '#.###'], ['#...#', '.###.', '..#..', '#####', '..#..'],
  ['##.##', '#####', '.#.#.', '#####', '#.#.#'], ['.###.', '#...#', '#####', '..#..', '#.#.#'],
];

/* ================= Sky ================= */
const SKY = ['#05051a', '#080824', '#0c0a2e', '#110c38', '#170e42', '#1e104b', '#271253', '#31145a',
  '#3d1660', '#4b1864', '#5b1a67', '#6c1d69', '#7e2269', '#902868', '#a03167', '#ae3c67', '#bb4969', '#c6586c'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 200), 1.3) * 0.84;
    v += Math.max(0, 1 - Math.hypot((i - 262) / 290, (y - 196) / 105)) * 0.26;      // warm pink city glow
    v -= Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.04;
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{ const r = rng(12); while (STARS.length < 22) { const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 96); if (x < 110) continue; STARS.push({ x, y, k: 20 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.45 }); } }

/** thin crescent with a faint earthshine disc */
function renderMoon() {
  const r = 7, s = 17, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 8, dy = y - 8, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    const sh = Math.hypot(dx + 3.4, dy + 2.6);
    if (sh < r - 0.2) { p.set(i, y, dd > r - 0.9 ? '#2c2458' : '#201a48'); continue; }
    let col = sh < r + 1.1 ? '#f2dfb8' : '#fff4d8';
    if (dd > r - 0.9) col = '#dcc49a';
    if ((dx - 2) ** 2 + (dy - 3) ** 2 < 1.3) col = '#e6d2aa';
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Clouds (tileable stratus, lit pink from below by the city) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: 4, h: 50, banks: 3, k: 1, rx: [12, 24], ry: [3, 6],
    pal: { top: '#2e2566', body: '#1a1446', core: '#15103e', bot: '#301c5c', bot2: '#241752' } },
  { seed: 29, y: 72, h: 58, banks: 4, k: 2, rx: [10, 22], ry: [3.2, 6.5],
    pal: { top: '#56398a', body: '#33205e', core: '#2b1a55', bot: '#a24a86', bot2: '#6c3274' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 120, base = h * (0.45 + r() * 0.3);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.3, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.5 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Mt. Fuji (far, cool, moonlit snowcap) ================= */
// A near-symmetric cone: straight, faintly concave flanks under a flat summit with one small crater notch.
// The snow cap ends on a level line that only thin ravine streaks break, in V-shaped drips (no scallops).
const fujiHW = dy => 3.5 + dy * 1.45 + dy * dy * 0.012;           // half-width dy rows below the summit
const FUJI_SNOW = 9;                                             // snow cap depth (rows) above the ravines
function fujiSnowLine(dx) {
  const ph = ((dx % 5) + 5) % 5, run = Math.floor(dx / 5);       // one ravine every 5 columns
  const len = Math.abs(dx) < 3 ? 0 : 1 + Math.floor(hash(run, 7, 9) * 3.2);   // none right under the crater
  const d = ph === 2 ? len : (ph === 1 || ph === 3) ? len >> 1 : 0;
  return FUJI.y + FUJI_SNOW + d;
}
function renderFuji() {
  const p = pix(W, H);
  for (let y = FUJI.y; y < 200; y++) {
    const dy = y - FUJI.y, hw = fujiHW(dy), hwUp = dy > 1 ? fujiHW(dy - 1) : dy ? 2 : -1;   // row above's reach
    for (let i = Math.floor(FUJI.x - hw); i <= Math.ceil(FUJI.x + hw); i++) {
      const dx = i - FUJI.x, ax = Math.abs(dx);
      if (ax > hw) continue;
      if (dy === 0 && (ax > 2 || dx === 1)) continue;             // flat top: soft shoulders, crater notch
      const sl = fujiSnowLine(dx);
      let col;
      if (y < sl) {
        const lv = 0.62 - dx / (hw + 3) * 0.7 - dy * 0.015 - (y >= FUJI.y + FUJI_SNOW ? 0.18 : 0);
        col = pick(['#4e5288', '#7278ac', '#a2a8d4', '#d8dcf4', '#f2f4ff'], lv, i, y);
      } else {
        const lv = 0.3 - dx / (hw + 4) * 0.25 + clamp(dy / 36, 0, 1) * 0.55;
        col = pick(['#1a1438', '#221a46', '#2c2052', '#39275c', '#4a2e66'], lv, i, y);
      }
      if (dx < 0 && ax > hwUp && y < sl + 3) col = '#e8ecff';     // moonlit rim (every stair tread)
      p.set(i, y, col);
    }
  }
  return trim(p);
}

/* ================= Tokyo Skytree (far, slim, two observation rings) ================= */
function stW(y) {                                     // full (odd) width at row y
  if (y < 67) return 1;
  if (y < 89) return 3;
  if (y < 93) return 7;                               // upper ring (galleria)
  if (y < 110) return 3;
  if (y < 116) return 9;                              // main observation deck
  if (y < 130) return 5;                              // the tube flares gently from here to its feet
  if (y < 147) return 7;
  if (y < 162) return 9;
  return 11;
}
const isRing = y => (y >= 89 && y < 93) || (y >= 110 && y < 116);
const ST_LAT = ['#2e3668', '#3a4480', '#4a5698', '#6a78bc'];
function renderSkytree() {
  const p = pix(W, H), x0 = SKYT.x;
  for (let y = SKYT.top; y < 196; y++) {
    const w = stW(y), hw = (w - 1) / 2;
    for (let i = -hw; i <= hw; i++) {
      let col;
      if (isRing(y)) {
        const top = y === 89 || y === 110, bot = y === 92 || y === 115;
        col = top ? '#b4c2ee' : bot ? '#343c72' : (i + y) % 3 === 0 ? '#6a78b8' : '#e2f4ff';
        if (Math.abs(i) === hw && !top) col = i < 0 ? '#8898d0' : '#343c72';
      } else if (w === 1) col = y < 53 ? '#eef4ff' : (y & 1 ? '#8a9ad8' : '#6a78bc');
      else if (i === -hw) col = '#8a9ce0';
      else if (i === hw) col = '#2e3668';
      else {                                           // lattice checker, lit left third, shaded right third
        const b = w <= 5 || Math.abs(i) * 3 <= hw ? 1 : i < 0 ? 2 : 0;
        col = ST_LAT[b + ((i + y) & 1)];
      }
      p.set(x0 + i, y, col);
    }
  }
  return trim(p);
}

/* ================= Far skyline (low, hazy, tiny windows) ================= */
function renderFarCity() {
  const p = pix(W, H), r = rng(23), wins = [], masts = [];
  let x = 100;
  while (x < W) {
    const bw = 5 + Math.floor(r() * 14);
    let top = 163 + Math.floor(r() * 18);
    if (x + bw > 140 && x < 160) top = Math.max(top, 175);        // keep the Skytree clear
    if (x + bw > 298 && x < 380) top = Math.max(top, 169);        // keep Fuji's slopes
    for (let y = top; y < 200; y++) for (let i = x; i < x + bw; i++) {
      let col = (i + y) % 7 === 0 ? '#1f183a' : '#1c1636';
      if (y === top) col = '#2e2454'; else if (i === x) col = '#251d46';
      p.set(i, y, col);
    }
    for (let wy = top + 2; wy < 196; wy += 3) for (let wx = x + 1; wx < x + bw - 1; wx += 2)
      if (r() < 0.3) wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 6), p: 0.55, off: r(), col: r() < 0.6 ? '#b88862' : '#8a92c6' });
    if (r() < 0.22) {
      const mx = x + (bw >> 1), mh = 3 + Math.floor(r() * 4);
      for (let y = top - mh; y < top; y++) p.set(mx, y, '#2a2248');
      masts.push([mx, top - mh - 1]);
    }
    x += bw;
  }
  return { c: trim(p), wins, masts };
}

/* ================= High-rises around the tower (window floors toggle) ================= */
const HRISE = [
  { x: 106, w: 28, top: 86, crown: 'flat', cool: 0.7, blink: true },
  { x: 160, w: 20, top: 128, crown: 'step', cool: 0.3 },
  { x: 186, w: 26, top: 108, crown: 'twin', cool: 0.8, blink: true },
  { x: 272, w: 22, top: 120, crown: 'slant', cool: 0.5, blink: true },
  { x: 292, w: 10, top: 150, crown: 'flat', cool: 0.2 },
  { x: 384, w: 18, top: 102, crown: 'spire', cool: 0.6, blink: true },
  { x: 406, w: 30, top: 118, crown: 'flat', cool: 0.4, blink: true },
  { x: 440, w: 40, top: 92, crown: 'flat', cool: 0.7, blink: true },
];
function hrTop(b, i) {                                // roof line of building b at local column i
  switch (b.crown) {
    case 'step': return b.top + (i < 3 || i >= b.w - 3 ? 6 : 0);
    case 'twin': return b.top + (i >= b.w / 2 - 3 && i < b.w / 2 + 3 ? 9 : 0);
    case 'slant': return b.top + Math.round((b.w - 1 - i) * 0.45);
    case 'spire': { const c = (b.w - 1) / 2, d = Math.abs(i - c); return b.top + Math.round(Math.max(0, d - 1) * 1.6); }
    default: return b.top;
  }
}
function renderHighrises() {
  const p = pix(W, H), lit = pix(W, H), r = rng(41), segs = [], blinks = [];
  const warm = ['#ffd694', '#ffc878', '#ffe2b0'], cool = ['#cfe6ff', '#b4d0ff', '#e6f2ff'];
  for (const b of HRISE) {
    for (let i = 0; i < b.w; i++) {
      const top = hrTop(b, i), gx = b.x + i;
      for (let y = top; y < 200; y++) {
        let col = (i & 1) ? '#1a1636' : '#1d1939';
        if (i === 0) col = '#2e2858'; else if (i === b.w - 1) col = '#120f26';
        if (y === top) col = '#3c3468';
        p.set(gx, y, col);
      }
    }
    if (b.crown === 'spire') {
      const c = b.x + ((b.w - 1) >> 1);
      for (let y = b.top - 12; y < b.top; y++) p.set(c, y, y % 3 ? '#2a2450' : '#3a3464');
      blinks.push([c, b.top - 13]);
    } else if (b.blink) blinks.push([b.x + 2, hrTop(b, 2) - 1], [b.x + b.w - 3, hrTop(b, b.w - 3) - 1]);
    if (b.crown === 'twin' || b.crown === 'slant') for (let i = 1; i < b.w - 1; i++) p.set(b.x + i, hrTop(b, i), b.crown === 'twin' ? '#8ae4ff' : '#d49aff');
    for (let y = b.top + 5; y < 198; y += 3) {
      let x = b.x + 2;
      while (x < b.x + b.w - 2) {
        const len = Math.min(b.x + b.w - 2 - x, 4 + Math.floor(r() * 8));
        const col = (r() < b.cool ? cool : warm)[Math.floor(r() * 3)];
        let any = false;
        for (let k = 0; k < len; k += 2) {
          const gx = x + k;
          if (y < hrTop(b, gx - b.x) + 3) continue;
          p.set(gx, y, '#0f0c22'); lit.set(gx, y, col); any = true;
        }
        if (any) segs.push({ id: segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.45, off: r() });
        x += len;
      }
    }
  }
  return { c: trim(p), lit: lit.done(), segs, blinks };
}

/* ================= Near city behind the viaduct (rooftops, tanks, small windows) ================= */
const NSIGN_X = [118, 198, 291, 452];                // columns for small vertical neon strips (facade or rooftop)
const NSIGN_COL = [['#ff5ab4', 'pink'], ['#5ee8ff', 'cyan'], ['#c070ff', 'violet'], ['#ffe060', 'yellow']];
// board: false is the skyline without the billboard, as the loop videos show it: the old slide sign stands in
// its place (as before the billboard came)
function renderNearCity(board) {
  const p = pix(W, H), r = rng(57), wins = [], blinks = [], tops = new Array(W).fill(999), signs = [];
  let x = 100;
  while (x < W) {
    const bw = 12 + Math.floor(r() * 22);
    let top = 158 + Math.floor(r() * 20);
    if (x + bw > 212 && x < 282) top = Math.max(top, 176);         // keep the tower's legs clear
    if (x + bw > 142 && x < 158) top = Math.max(top, 176);         // and the Skytree's shaft
    if (x + bw > 300 && x < 378) top = Math.max(top, 175);         // and Fuji's slopes
    const shade = r() < 0.5 ? '#16122b' : '#19142f';
    for (let i = x; i < Math.min(W, x + bw); i++) tops[i] = top;
    for (let y = top; y < 200; y++) for (let i = x; i < x + bw; i++) {
      let col = shade;
      if (y === top) col = '#2c2548'; else if (i === x) col = '#221c3c'; else if (i === x + bw - 1) col = '#100d20';
      p.set(i, y, col);
    }
    for (let i = x + 1; i < x + bw - 1; i += 2) if (r() < 0.7) p.set(i, top - 1, '#241e3e');   // railing
    if (r() < 0.45) {                                                                          // water tank
      const tx = x + 2 + Math.floor(r() * Math.max(1, bw - 9));
      for (let y = top - 6; y < top - 2; y++) for (let i = tx; i < tx + 5; i++) p.set(i, y, i === tx ? '#3a3260' : '#241e40');
      p.set(tx, top - 2, '#1c1834'); p.set(tx + 4, top - 2, '#1c1834'); p.set(tx, top - 1, '#1c1834'); p.set(tx + 4, top - 1, '#1c1834');
    }
    if (r() < 0.4) { const ax = x + 2 + Math.floor(r() * (bw - 5)); p.fill(ax, top - 2, 3, 2, '#221c3a'); p.set(ax, top - 2, '#3a3258'); }
    if (r() < 0.2 && top < 172) { const mx = x + (bw >> 1); for (let y = top - 8; y < top; y++) p.set(mx, y, '#2a2446'); blinks.push([mx, top - 9]); }
    for (let wy = top + 3; wy < 196; wy += 5) for (let wx = x + 2; wx < x + bw - 2; wx += 3) {
      if (r() > 0.55) continue;
      p.fill(wx, wy, 1, 2, '#0f0c1e');
      wins.push({ id: wins.length, x: wx, y: wy, n: 3 + Math.floor(r() * 9), p: 0.35 + r() * 0.3, off: r(),
        col: r() < 0.65 ? (r() < 0.5 ? '#ffcf80' : '#ffb45e') : '#b0ccff' });
    }
    x += bw;
  }
  // billboard frames (content is drawn per frame)
  for (const b of board ? BILLBOARDS : [SLIDE_BOX, ...BILLBOARDS]) {
    p.fill(b.x - 1, b.y - 1, b.w + 2, b.h + 2, '#1a1428');
    p.fill(b.x - 1, b.y - 1, b.w + 2, 1, '#3a3050');
    for (const lx of [b.x + 3, b.x + b.w - 4]) for (let y = b.y + b.h + 1; y < 184; y++) p.set(lx, y, (y & 1) ? '#1e1830' : '#2a2242');
    for (let i = b.x + 3; i <= b.x + b.w - 4; i++) if ((i + b.y) % 3 === 0) p.set(i, b.y + b.h + 3, '#1e1830');
  }
  if (board) renderBBFrame(p);
  for (const sx of NSIGN_X) {
    const roof = Math.max(tops[sx], tops[sx + 3]), onRoof = roof > 168;
    const [col, hl] = NSIGN_COL[signs.length % NSIGN_COL.length], c = hex(col);
    const y0 = onRoof ? roof - 14 : roof + 2, h = onRoof ? 12 : Math.min(14, 183 - y0);
    if (onRoof) for (let y = y0 + h; y < roof; y++) { p.set(sx, y, '#1c1630'); p.set(sx + 3, y, '#1c1630'); }
    p.fill(sx, y0, 4, h, '#120c1a');
    for (let y = y0 + 1; y < y0 + h - 1; y++) for (let i = sx + 1; i < sx + 3; i++)
      p.set(i, y, hash(i, y, 7) < 0.62 ? c : [c[0] * 0.35 | 0, c[1] * 0.35 | 0, c[2] * 0.35 | 0]);
    signs.push({ id: signs.length, x: sx, y: y0, h, hl });
  }
  return { c: trim(p), wins, blinks, signs };
}

/* ================= Small rooftop sign boxes ================= */
const BILLBOARDS = [
  { x: 264, y: 150, w: 26, h: 10, kind: 'chaser' },
];
function renderChaser(on) {
  const b = BILLBOARDS[0], p = pix(b.w, b.h);
  p.fill(0, 0, b.w, b.h, '#200a14');
  [0, 1, 2, 3].forEach(k => glyphP(p, G5[(k * 3 + 1) % G5.length], 2 + k * 6, 2, on ? '#ff5a4a' : '#4a1a1c'));
  return p.done();
}
// the slide sign that stood below the big billboard's spot before it came: three glyph slides, 8 s each, with a
// quick wipe. Only the skyline without the billboard shows it (the loop videos, see renderNearCity).
const SLIDE_BOX = { x: 156, y: 146, w: 34, h: 13, kind: 'slides' };
const SLIDES = [
  { bg: ['#ff3f9a', '#ff66b0', '#ff8cc6'], ink: '#fff2fa', glow: '#ff4fa8', g: [0, 3, 5] },
  { bg: ['#1aa8e8', '#2ec6ff', '#7ae2ff'], ink: '#0a1e3c', glow: '#40d0ff', g: [7, 2, 6] },
  { bg: ['#ff9a1e', '#ffbe3a', '#ffe070'], ink: '#6a1408', glow: '#ffb040', g: [4, 1, 0] },
];
function renderSlide(s) {
  const b = SLIDE_BOX, p = pix(b.w, b.h);
  for (let y = 0; y < b.h; y++) for (let i = 0; i < b.w; i++) p.set(i, y, pick(s.bg, 1 - Math.abs(i - b.w / 2) / (b.w / 2) * 0.8 - y / b.h * 0.3, i, y));
  s.g.forEach((gi, k) => glyphP(p, G7[gi], 3 + k * 10, 3, s.ink));
  return p.done();
}
function drawSlides(t) {
  const b = SLIDE_BOX, sI = step(t, 30), f = frac(t / 8), nx = (sI + 1) % 3;
  ctx.drawImage(SLIDE_C[sI % 3], b.x, b.y);
  if (f > 0.94) { const wpx = Math.round((f - 0.94) / 0.06 * b.w); ctx.drawImage(SLIDE_C[nx], 0, 0, wpx, b.h, b.x, b.y, wpx, b.h); }
  halo(HL.slide[sI % 3], b.x + b.w / 2, b.y + b.h / 2, 0.9);
}

/* ================= The trackside billboard (a lit poster on one steel column) ================= */
// Face BB stands on a column that rises from beyond the tracks, in the catenary masts' plane and in front of the near
// city, so it is read at the railway's scale: the 16 px face is about as high as a car of the train that passes under
// it (18 px). No roof in this part of the frame is high enough to carry it: the face has to end by y = 148 and the
// wires run at 157 to 163. A trough under the hood lights the poster from above, as the site's faces are lit: no
// floodlamps, no aviation lights. Its own ad is an invented cherry-blossom soda (can + lettering, fizz, a shine
// sweep), night-graded to stay well under Tokyo Tower; promo captures paint the pitch on the face instead.
const BB_TEAL = ['#0c3048', '#134f66', '#1b7082', '#2a93a0', '#4ab6b2', '#7ad4c4'];
const BB_NIGHT = '#1c1640';                                    // the scene's night, which the ad is graded towards
const BB_GRADE = { back: { level: 0.58, tint: BB_NIGHT, mix: 0.2 }, top: { level: 0.78, tint: BB_NIGHT, mix: 0.1 } };
const CAN = { x: 2, y: 2 };                                    // 8 x 12 can, top-left inside the face
const CAN_BODY = ['#6a1440', '#b82c6c', '#ff5fa2', '#ffaad0', '#ff7ab4', '#e0488c', '#a82462', '#5a1038'];
const CAN_BAND = ['#8a86a4', '#d8d4e6', '#ffffff', '#ffffff', '#f2eef8', '#dcd8e8', '#b8b4cc', '#7a7694'];
const BB_BOLD = {                                              // 6 x 7 letters with 2 px stems, for the big word
  S: ['.#####', '##....', '##....', '.####.', '....##', '....##', '#####.'],
  O: ['.####.', '##..##', '##..##', '##..##', '##..##', '##..##', '.####.'],
  D: ['#####.', '##..##', '##..##', '##..##', '##..##', '##..##', '#####.'],
  A: ['.####.', '##..##', '##..##', '######', '##..##', '##..##', '##..##'],
};
const BB_BUBBLES = [];
{ const r = rng(211); for (let i = 0; i < 8; i++) BB_BUBBLES.push({ id: i, n: 50 + Math.floor(r() * 30), ph: r(), k: 280 + Math.floor(r() * 90) }); }
/** light on the poster: brightest under the hood's trough, falling off down the face */
function bbLight(i, j) {
  return 0.16 + 0.5 * (1 - j / (BB.h - 1)) ** 1.4
    + Math.max(0, 1 - Math.hypot(i - CAN.x - 4, j - CAN.y - 6) / 9) * 0.3;          // printed burst behind the can
}
/** posterised ramp: flat bands with a narrow dither seam (a printed poster, not a gradient) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
function renderBBBack() {
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) p.set(i, j, pickFlat(BB_TEAL, bbLight(i, j), i, j));
  return gradeAd(p.done(), BB_GRADE.back);
}
/** lettering with a drop shadow (offset dx, 1), FONT3 or the rows of `font`; `shine` also marks it in the shine mask */
function bbText(p, m, s, x, y, col, sh, shine, dx, font) {
  for (const pass of [0, 1]) {
    let cx = x;
    for (const ch of s) {
      const g = font ? font[ch] : FONT3[ch];
      g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1' || row[i] === '#') {
        if (pass === 0) p.set(cx + i + dx, y + j + 1, sh);
        else { p.set(cx + i, y + j, col); if (shine) m.set(cx + i, y + j, '#ffffff'); }
      } });
      cx += g[0].length + (font ? 2 : 1);
    }
  }
}
function renderBBTop() {                                       // can + lettering (transparent elsewhere) and their shine mask
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h), X = CAN.x, Y = CAN.y;
  for (let k = 0; k < 8; k++) {
    const edge = k === 0 || k === 7;
    if (!edge) p.set(X + k, Y, k < 4 ? '#eef2fa' : '#b4b8cc');                   // top rim
    p.set(X + k, Y + 1, edge ? '#4a4e66' : '#8a90a8');                           // lid
    for (let j = Y + 2; j <= Y + 10; j++) {
      p.set(X + k, j, (j >= Y + 4 && j <= Y + 6 ? CAN_BAND : CAN_BODY)[k]);
      m.set(X + k, j, '#ffffff');
    }
    if (!edge) p.set(X + k, Y + 11, k < 4 ? '#9aa0b8' : '#6a6e88');              // bottom rim
  }
  for (const [i, j, c] of [[3, 4, '#ff5fa2'], [2, 5, '#ff5fa2'], [4, 5, '#ff5fa2'], [3, 6, '#ff5fa2'], [3, 5, '#ffd060']]) p.set(X + i, Y + j, c);
  for (const [i, j] of [[1, 3], [5, 8], [2, 9], [6, 2]]) p.set(X + i, Y + j, '#ffd6ea');     // condensation drops
  bbText(p, m, 'SAKURA', 15, 2, '#ffd2e6', '#0a2438', false, 0);
  bbText(p, m, 'SODA', 12, 8, '#ffffff', '#b8246c', true, 1, BB_BOLD);
  return { top: gradeAd(p.done(), BB_GRADE.top), mask: m.done() };
}
/** static steel: the frame box under its hood, a service ledge, and the column with two knee braces */
function renderBBFrame(p) {
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h, ledge = y1 + 1;
  const c0 = BB_POST.x, c1 = c0 + BB_POST.w - 1;
  // the column, from under the ledge down behind the viaduct's parapet: the masts, the wires and the train pass in front
  for (let y = ledge + 1; y < 184; y++) {
    p.set(c0, y, y < ledge + 6 ? '#5e5484' : (y & 3) === 0 ? '#4a4272' : '#423a68');   // lit edge, brighter under the board
    for (let i = c0 + 1; i < c1; i++) p.set(i, y, '#2c2650');
    p.set(c1, y, '#18142c');
  }
  for (let k = 1; k <= 5; k++) { p.set(c0 - k, ledge + 6 - k, '#463e6c'); p.set(c1 + k, ledge + 6 - k, '#2e2850'); }   // knee braces
  // frame box (the face itself is painted per frame)
  p.fill(x0, y0, BB.w + 2, BB.h + 2, '#1a1428');
  for (let i = x0; i <= x1; i++) { p.set(i, y0, '#3e3458'); p.set(i, y1, '#2c2440'); }
  for (let y = y0; y <= y1; y++) { p.set(x0, y, '#2c2444'); p.set(x1, y, '#110d1e'); }
  for (let i = x0; i <= x1; i++) p.set(i, y0 - 1, (i - x0) % 4 === 0 ? '#3a3258' : '#241e3a');     // hood over the light trough
  for (let i = x0 - 1; i <= x1 + 1; i++) p.set(i, ledge, (i & 1) ? '#3a3456' : '#2a2442');          // service ledge
}
function drawBillboard(t, pitch) {
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + BB.h / 2);                             // a faint, steady spill on the buildings behind
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(BBAD.back, BB.x, BB.y);
    ctx.fillStyle = '#9fd8cc';                                                   // fizz rising behind the can and the lettering
    ctx.globalAlpha = 0.6;
    for (const b of BB_BUBBLES) {
      const { cyc, u } = cycle(t, b.n, b.ph);
      const x = 1 + Math.floor(hash(b.id, cyc, 5) * 10), y = Math.round(BB.h - u * (BB.h + 1));
      if (y < 0 || y >= BB.h) continue;
      ctx.fillRect(BB.x + x + Math.round(wave(t, b.k, b.ph) * 0.7), BB.y + y, 1, 1);
    }
    ctx.globalAlpha = 1;
    ctx.drawImage(BBAD.top, BB.x, BB.y);
    const f = frac(t / 12);                                                      // a gloss sheen rolls over the poster
    if (f < 0.12) {
      const bx = lerp(-12, BB.w + 12, f / 0.12);
      ctx.fillStyle = '#ffffff';
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a = Math.max(0, sx), b = Math.min(BB.w, sx + 3);
        if (b <= a) continue;
        ctx.globalAlpha = 0.1; ctx.fillRect(BB.x + a, BB.y + j, b - a, 1);
        ctx.globalAlpha = 0.4; ctx.drawImage(BBAD.mask, a, j, b - a, 1, BB.x + a, BB.y + j, b - a, 1);
      }
      ctx.globalAlpha = 1;
    }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
}

/* ================= Tokyo Tower (orange/white banded lattice, two decks, antenna) ================= */
const TKEY = [[192, 24], [176, 19], [160, 14.6], [144, 11.2], [132, 9.3], [122, 8], [113, 6.4], [100, 5.1],
  [88, 4.1], [76, 3.2], [69, 2.6], [62, 2], [55, 1.4]];
function towerHW(y) {
  if (y >= TKEY[0][0]) return TKEY[0][1];
  for (let i = 0; i < TKEY.length - 1; i++) {
    const [y0, w0] = TKEY[i], [y1, w1] = TKEY[i + 1];
    if (y <= y0 && y >= y1) return w0 + (w1 - w0) * (y0 - y) / (y0 - y1);
  }
  return 0;
}
const TWHITE = [[55, 59], [65, 69], [82, 85], [94, 97], [108, 113], [132, 136], [153, 157]];   // white bands
const isWhite = y => TWHITE.some(([a, b]) => y >= a && y <= b);
const TRUNGS = [192, 184, 176, 168, 160, 157, 153, 146, 140, 136, 132, 127, 122, 113, 108, 103, 97, 94, 89, 85, 82, 76, 69, 65, 62, 59, 55];
const T_OR = ['#4a140c', '#7c2210', '#b43614', '#e2521c', '#ff7a2c', '#ffa452', '#ffd08a'];
const T_WH = ['#5a3028', '#946050', '#c89078', '#ecbea0', '#ffe0c4', '#fff2e2', '#ffffff'];
function renderTower() {
  const ox = TWR.cx - 27, oy = TWR.ant - 2, w = 55, h = TWR.base - oy + 1;
  const p = pix(w, h), cx = TWR.cx;
  const put = (gx, gy, col, a = 255) => p.set(gx - ox, gy - oy, col, a);
  for (let gy = TWR.lat; gy <= TWR.base; gy++) {
    if ((gy >= TWR.deck - 1 && gy <= TWR.deckB) || (gy >= TWR.top - 1 && gy <= TWR.topB)) continue;
    const hw = towerHW(gy + 0.5), white = isWhite(gy), ramp = white ? T_WH : T_OR, fh = Math.floor(hw);
    const rung = TRUNGS.includes(gy);
    // Landmark Light: floodlit hardest just under each deck and at the feet
    const flood = gy > TWR.deckB ? 0.55 * smooth(162, 124, gy) + 0.3 * smooth(172, 192, gy) : gy > TWR.topB ? 0.45 * smooth(104, 78, gy) : 0.35;
    for (let dx = -fh; dx <= fh; dx++) {
      const gx = cx + dx, ax = Math.abs(dx), L = dx < 0;
      if (gy > 170) {                                  // the arch between the feet
        const e = (dx / 14) ** 2 + ((TWR.base + 1 - gy) / 22) ** 2;
        if (e < 1) continue;
        if (e < 1.3) { put(gx, gy, pick(ramp, ((L ? 4.3 : 3.5) + flood) / 6, gx, gy)); continue; }
      }
      let lv;
      if (ax === fh) lv = L ? 4.4 : 3.5;              // outer legs
      else if (ax === 0) lv = 5.0;                     // front corner leg catches the floodlight
      else if (hw < 3.6) lv = rung || (gy & 1) ? (L ? 3.4 : 2.7) : (L ? 2.8 : 2.2);
      else {                                           // regular diagonal truss, rungs at the band edges
        const member = rung || ((ax + gy) & 3) === 0 || ((ax - gy) & 3) === 0 || (hw > 9 && ax === Math.round(hw / 2));
        if (!member) { put(gx, gy, white ? '#44201e' : '#380e0e', 135); continue; }
        lv = L ? 3.3 : 2.5;
      }
      put(gx, gy, pick(ramp, (lv + flood) / 6, gx, gy));
    }
  }
  // main deck (two lit floors)
  for (let dx = -11; dx <= 11; dx++) {
    const gx = cx + dx, L = dx < 0, end = Math.abs(dx) === 11, mull = (dx + 11) % 3 === 0;
    if (Math.abs(dx) <= 10) put(gx, TWR.deck - 1, L ? '#f2dcc6' : '#c09a84');
    put(gx, TWR.deck, L ? '#fff2e0' : '#e8c8b0');
    put(gx, TWR.deck + 1, L ? '#7a4a3a' : '#5a3228');
    for (const y of [TWR.deck + 2, TWR.deck + 3, TWR.deck + 5, TWR.deck + 6]) {
      const up = y < TWR.deck + 4;
      put(gx, y, end ? (L ? '#e8d0b8' : '#a07860') : mull ? (L ? '#b07850' : '#8a5a40')
        : up ? (L ? '#fff0c0' : '#ffd898') : (L ? '#ffe2a0' : '#f4c47c'));
    }
    put(gx, TWR.deck + 4, L ? '#f4e2cc' : '#c8a48a');
    put(gx, TWR.deckB, L ? '#d89a70' : '#a86a4a');
  }
  // top deck (cool white interior)
  for (let dx = -5; dx <= 5; dx++) {
    const gx = cx + dx, L = dx < 0, end = Math.abs(dx) === 5;
    if (Math.abs(dx) <= 4) put(gx, TWR.top - 1, L ? '#ffe8d4' : '#c8a08a');
    put(gx, TWR.top, L ? '#fff4e8' : '#e0c0a8');
    for (let y = TWR.top + 1; y <= TWR.top + 3; y++) put(gx, y, end ? (L ? '#e0c8b8' : '#9a7462') : (dx + 5) % 3 === 0 ? '#8a90b0' : (L ? '#f0f8ff' : '#c8e0ff'));
    put(gx, TWR.top + 4, L ? '#f0dcc8' : '#b89480');
    put(gx, TWR.topB, L ? '#d8906a' : '#a0603e');
  }
  // antenna: candy-striped mast with a small platform
  for (let gy = TWR.ant; gy < TWR.lat; gy++) {
    const wb = Math.floor((gy - TWR.ant) / 3) % 2 === 0, hw = gy >= 46 ? 1 : 0;
    for (let dx = -hw; dx <= hw; dx++) put(cx + dx, gy, wb ? (dx > 0 ? '#e8c8b0' : '#fff4e8') : (dx > 0 ? '#c84a1e' : '#ff7a34'));
  }
  for (let dx = -2; dx <= 2; dx++) put(cx + dx, 46, dx < 1 ? '#ffc488' : '#d88a50');
  return { c: p.done(), ox, oy };
}

/* ================= Brick viaduct with shops under the arches (gado-shita) ================= */
const ARCH = { x0: 100, period: 32, pier: 6 };
const BAYS = [];
{
  const kinds = ['izakaya', 'bar', 'izakaya', 'shutter', 'izakaya', 'ramen', 'shutter', 'bar', 'izakaya', 'dark', 'izakaya', 'bar'];
  for (let k = 0, x = ARCH.x0; x < W; k++, x += ARCH.period) BAYS.push({ id: k, x: x + ARCH.pier, w: ARCH.period - ARCH.pier, kind: kinds[k % kinds.length] });
}
const archTop = (mx, w) => { const c = (w - 1) / 2, d = Math.abs(mx - c) / (c + 1); return 201 + Math.floor((1 - Math.sqrt(Math.max(0, 1 - d * d))) * 7); };
const MASTS = [132, 204, 276, 348, 420];
const PLAMPS = [148, 212, 308, 372, 436];            // small lamps on the viaduct parapet
function renderViaduct() {
  const p = pix(W, H), r = rng(74);
  for (let x = 0; x < W; x++) {
    p.set(x, 184, '#77709a'); p.set(x, 185, '#524c74');
    for (let y = 186; y <= 189; y++) p.set(x, y, x % 24 === 0 ? '#2c2846' : (y === 189 ? '#302c4a' : '#3c3860'));
    p.set(x, 190, '#1c1a2e');
    for (let y = 191; y <= 197; y++) p.set(x, y, x % 48 === 0 ? '#24203a' : pick(['#2a2742', '#302c4a'], 0.5 + (hash(x, y, 2) - 0.5) * 0.7, x, y));
    p.set(x, 198, '#4a4466');
    if (x % 16 === 0) for (let y = 191; y <= 197; y++) { p.set(x, y, '#3c3858'); p.set(x + 1, y, '#1e1c30'); }
    if (x % 4 === 2) { p.set(x, 192, '#46426a'); p.set(x, 196, '#46426a'); }
  }
  for (const x of PLAMPS) { p.fill(x, 182, 1, 3, '#2a2640'); p.fill(x - 1, 181, 3, 1, '#2a2640'); p.set(x, 182, '#ffe0a0'); }
  for (let x = 0; x < W; x++) {
    const m = ((x - ARCH.x0) % ARCH.period + ARCH.period) % ARCH.period, mx = m - ARCH.pier, bw = ARCH.period - ARCH.pier;
    for (let y = 199; y < GROUND; y++) {
      if (m >= ARCH.pier && y >= archTop(mx, bw)) continue;
      const row = (y - 199) >> 1;
      let col = (y - 199) % 2 === 1 || (x + (row & 1) * 2) % 4 === 0 ? '#2a1418' : (hash(x >> 2, row, 5) < 0.3 ? '#5a2c2a' : '#4a2426');
      if (m >= ARCH.pier && y === archTop(mx, bw) - 1) col = '#7a4236';                  // lit voussoirs
      if (m === ARCH.pier - 1 || m === 0) col = (y & 1) ? '#3a1c1e' : '#2a1418';
      p.set(x, y, col);
    }
  }
  for (const b of BAYS) renderBay(p, b, r);
  // catenary wires (drawn over the train): messenger with sag, contact wire, droppers
  for (let x = 0; x < W; x++) {
    let a = -1e9, b2 = 1e9;
    for (const m of MASTS) { if (m <= x) a = Math.max(a, m); else b2 = Math.min(b2, m); }
    if (a < -1e8) a = MASTS[0] - 72; if (b2 > 1e8) b2 = MASTS[MASTS.length - 1] + 72;
    const sag = Math.round(2.2 * Math.sin(Math.PI * (x - a) / (b2 - a)));
    p.set(x, 157 + sag, '#17152a');
    p.set(x, 163, '#1c1a30');
  }
  return trim(p);
}
function renderBay(p, b, r) {
  const x0 = b.x, x1 = b.x + b.w - 1, top = i => archTop(i - x0, b.w);
  const inside = (i, y) => i >= x0 && i <= x1 && y >= top(i) && y < GROUND;
  const each = f => { for (let y = 199; y < GROUND; y++) for (let i = x0; i <= x1; i++) if (inside(i, y)) f(i, y); };
  if (b.kind === 'shutter') {
    each((i, y) => p.set(i, y, y === top(i) ? '#6a6680' : (y & 1) ? '#3c3a52' : '#4a4862'));
    p.fill(x0 + 10, 203, 6, 1, '#8a86a0');
    return;
  }
  if (b.kind === 'dark') { each((i, y) => p.set(i, y, (i + y) % 5 === 0 ? '#17142a' : '#100e1e')); return; }
  const bar = b.kind === 'bar';
  const ramp = bar ? ['#4a1848', '#6e2266', '#963286', '#c04aa4'] : ['#8a3c1c', '#c86a2c', '#f0a048', '#ffd07a'];
  each((i, y) => p.set(i, y, pick(ramp, 1 - (y - 201) / 15 * 0.8 - Math.abs(i - (x0 + x1) / 2) / b.w * 0.5, i, y)));
  // back shelf with bottles, counter, patrons
  for (let i = x0 + 3; i < x1 - 2; i++) { p.set(i, 205, bar ? '#3a1030' : '#6a3418'); if (r() < 0.6) p.set(i, 204, ['#3a8a4a', '#c83040', '#e8d070', '#6a4ab0', '#f0f0f0'][Math.floor(r() * 5)]); }
  for (let i = x0; i <= x1; i++) { p.set(i, 210, bar ? '#6a2a5a' : '#9a5a2a'); p.set(i, 211, bar ? '#2a0a24' : '#4a2410'); for (let y = 212; y < GROUND; y++) p.set(i, y, bar ? '#200818' : '#3a1c10'); }
  const nP = 2 + Math.floor(r() * 2);
  for (let k = 0; k < nP; k++) {
    const px0 = x0 + 4 + Math.floor((k + 0.5) * (b.w - 8) / nP);
    p.fill(px0, 206, 2, 2, bar ? '#2a0a26' : '#3a1a10'); p.fill(px0 - 1, 208, 4, 2, bar ? '#2a0a26' : '#3a1a10');
  }
  if (bar) { for (let i = x0 + 5; i < x1 - 4; i++) if ((i & 3) !== 0) p.set(i, 202, '#ff8ad8'); return; }
  // noren across the top of the opening + two little red lanterns
  const nc = r() < 0.5 ? ['#1e2656', '#2c3670'] : ['#a01e28', '#c42a34'];
  for (let i = x0 + 2; i <= x1 - 2; i++) {
    const slit = (i - x0 - 2) % 6 === 5;
    for (let y = top(i); y < 205; y++) if (!slit) p.set(i, y, y === 204 ? nc[0] : nc[1]);
    if (!slit && (i - x0) % 6 === 2) p.set(i, 203, '#f4ecde');
  }
  for (const lx of [x0 + 1, x1 - 1]) { p.set(lx, 204, '#2a1010'); p.fill(lx - 1 + (lx === x0 + 1 ? 1 : 0), 205, 2, 3, '#ff5a3a'); p.set(lx, 208, '#2a1010'); }
}
function renderMasts() {
  const p = pix(W, H);
  for (const m of MASTS) {
    for (let y = 152; y < 184; y++) { p.set(m, y, '#2c2846'); p.set(m + 1, y, (y & 3) === 0 ? '#3a3660' : '#4a4670'); }
    for (let i = m - 6; i <= m + 1; i++) { p.set(i, 155, '#2c2846'); if (i > m - 5) p.set(i, 159, '#2c2846'); }
    for (let k = 0; k < 4; k++) p.set(m - 5 + k, 159 - k, '#2c2846');
    p.set(m - 6, 156, '#9a96b8'); p.set(m - 4, 160, '#9a96b8');
  }
  return trim(p);
}

/* ================= Commuter train (silver body, green stripe) ================= */
const CAR = 58, GAP = 2, NCARS = 5, TLEN = NCARS * (CAR + GAP) - GAP;
function renderTrain() {
  const th = 24, p = pix(TLEN, th), r = rng(81);
  const inRange = (i, a, b) => i >= a && i <= b;
  for (let c = 0; c < NCARS; c++) {
    const x0 = c * (CAR + GAP), front = c === NCARS - 1, rear = c === 0;
    for (let i = 0; i < CAR; i++) {
      const gx = x0 + i, e = front ? CAR - 1 - i : rear ? i : 99;
      const roofY = e === 0 ? 9 : e === 1 ? 7 : 6;
      for (let y = roofY; y < th; y++) {
        let col;
        if (y === roofY) col = '#dde2ee';
        else if (y < 9) col = y === 7 ? '#a8aec2' : '#8e94a8';
        else if (y === 9) col = '#5e6478';
        else if (y === 10 || y === 16) col = '#c4cad8';
        else if (y <= 15) {
          const door = [6, 20, 34, 48].some(d => inRange(i, d, d + 3));
          if (door) { const dd = [6, 20, 34, 48].find(d => inRange(i, d, d + 3)), di = i - dd; col = di === 0 || di === 3 ? '#5a6072' : y <= 14 ? '#ffeec4' : '#b8becc'; }
          else if ((inRange(i, 11, 18) || inRange(i, 25, 32) || inRange(i, 39, 46) || inRange(i, 2, 3) || inRange(i, 53, 55)) && e > 1) col = y === 11 ? '#fff8e0' : '#ffe6b0';
          else col = '#c0c6d4';
        } else if (y <= 18) col = y === 17 ? '#94d44e' : '#6aa836';
        else col = y === th - 1 ? '#5e6478' : '#a2a8b8';
        if (e <= 1 && y > 9 && y < 17) col = y < 14 ? '#28303e' : '#9aa0b0';        // cab front window
        p.set(gx, y, col);
      }
    }
    // passengers: heads and shoulders in the lit windows
    for (const [a, b] of [[11, 18], [25, 32], [39, 46]]) for (let i = a; i < b; i++) {
      if (r() > 0.45) continue;
      const hy = 12 + (r() < 0.5 ? 0 : 1), col = r() < 0.5 ? '#3a2c34' : '#2c2230';
      p.set(x0 + i, hy, col); p.set(x0 + i, hy + 1, col);
      p.fill(x0 + i - (r() < 0.5 ? 1 : 0), hy + 2, 2, 15 - hy - 1, col);
      i += 1;
    }
    // roof AC units
    if (!front && !rear || true) for (const ax of [12, 38]) { p.fill(x0 + ax, 4, 9, 2, '#8a90a4'); p.fill(x0 + ax, 4, 9, 1, '#c4cad8'); }
    if (front) { p.fill(x0 + CAR - 2, 18, 2, 2, '#ffffff'); p.fill(x0 + 50, 7, 4, 1, '#ffa040'); }
    if (rear) p.fill(x0, 18, 2, 2, '#ff3434');
    if (c === 1 || c === 3) {                                         // single-arm pantograph
      const px0 = x0 + 28;
      p.fill(px0 - 2, 5, 7, 1, '#5a5e74');
      for (let k = 0; k < 4; k++) p.set(px0 + k, 4 - k, '#a4a8c0');
      p.set(px0 + 2, 1, '#a4a8c0');
      p.fill(px0, 1, 6, 1, '#c8ccde');
    }
  }
  for (let c = 1; c < NCARS; c++) for (let y = 9; y < th - 2; y++) for (let g = 0; g < GAP; g++) p.set(c * (CAR + GAP) - GAP + g, y, y < 11 ? '#3a3c4c' : '#23242f');
  const right = p.done();
  const [left, lx] = makeCanvas(TLEN, th);
  lx.translate(TLEN, 0); lx.scale(-1, 1); lx.drawImage(right, 0, 0);
  return { right, left };
}

/* ================= Wet street: sidewalk under the arches, asphalt, zebra crossing ================= */
const PUDDLES = [[166, 224, 20, 2], [334, 226, 24, 2], [206, 256, 30, 4], [300, 262, 22, 3], [58, 256, 26, 3], [430, 260, 20, 3]];
const inPuddle = (x, y) => PUDDLES.some(([cx, cy, rx, ry]) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1);
function zebraBar(xb, y) {                            // [centre x, half width] of the bar with bottom centre xb at row y
  const f = (y - ZEB.vy) / (ZEB.y1 - ZEB.vy);
  return [ZEB.vx + (xb - ZEB.vx) * f, 3.6 * f];
}
function renderStreet() {
  const p = pix(W, H), r = rng(66);
  for (let y = GROUND; y < H; y++) for (let x = 0; x < W; x++) {
    let col;
    if (y < 218) col = x % 8 === 0 || y === 216 ? '#282238' : '#322c48';
    else if (y === 218) col = '#5c5680';
    else if (y === 219) col = '#1a1628';
    else col = pick(['#121022', '#151228', '#19162e', '#1e1a34'], 0.45 - (y - 220) / 50 * 0.25 + (hash(x, y, 5) - 0.5) * 0.5, x, y);
    // sidewalks in front of the alley (left) and under the tree (right)
    if (y >= AB.base && x < 116 - (y - AB.base) * 0.4) col = (x + (y >> 2) * 3) % 12 === 0 || y % 6 === 0 ? '#221c30' : '#2c2640';
    if (y >= 247 && x > 380 + (y - 247) * 0.5) col = (x + (y >> 2) * 3) % 12 === 0 || y % 6 === 0 ? '#221c30' : '#2c2640';
    if (y === AB.base - 1 && x < 116) col = '#4c4666';
    p.set(x, y, col);
  }
  // curb edges of the two corner sidewalks
  for (let y = AB.base; y < H; y++) { const x = Math.round(116 - (y - AB.base) * 0.4); p.set(x, y, '#5a5478'); p.set(x + 1, y, '#141020'); }
  for (let y = 247; y < H; y++) { const x = Math.round(380 + (y - 247) * 0.5); p.set(x, y, '#5a5478'); p.set(x - 1, y, '#141020'); }
  // zebra crossing: bars converge to the vanishing point, worn paint
  for (let xb = ZEB.x0; xb <= ZEB.x1; xb += 12) for (let y = ZEB.y0; y <= ZEB.y1; y++) {
    const [xc, hw] = zebraBar(xb, y);
    for (let x = Math.floor(xc - hw - 1); x <= Math.ceil(xc + hw + 1); x++) {
      const cov = clamp(hw + 0.5 - Math.abs(x + 0.5 - xc), 0, 1);
      if (cov <= bayer(x, y) * 0.95) continue;
      const worn = hash(x, y, 13) < 0.16;
      p.set(x, y, worn ? '#6c6886' : (y === ZEB.y0 ? '#9894b2' : '#b4b0cc'));
    }
  }
  // puddle rims
  for (const [cx, cy, rx, ry] of PUDDLES) {
    for (let y = cy - ry; y <= cy + ry; y++) for (let x = cx - rx; x <= cx + rx; x++)
      if (((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) p.set(x, y, '#0c0a1a');
    for (let a = 0; a < 64; a++) { const t = a / 64 * TAU; if (Math.sin(t) > 0.3) continue; p.set(cx + Math.cos(t) * (rx + 1), cy + Math.sin(t) * (ry + 1), '#2e2a4c'); }
  }
  // fallen petals: dense under the tree, a few drifted across the crossing and the alley sidewalk
  for (let k = 0; k < 170; k++) {
    let x, y;
    if (k < 110) { x = 372 + r() * 110; y = 246 + r() * 24; }
    else if (k < 150) { x = 120 + r() * 260; y = 222 + r() * 48; }
    else { x = r() * 116; y = 246 + r() * 24; }
    const c = r() < 0.3 ? '#ffd6e6' : r() < 0.6 ? '#f39cc0' : '#c86a98';
    p.set(x, y, c); if (r() < 0.25) p.set(x + 1, y, c);
  }
  return trim(p);
}

/* ================= Pedestrian + car signals (60 s cycle, 4 per loop) ================= */
// car: green [0,26) + [52,60), yellow [26,29), red [29,52); walkers: green [30,46), blinking [46,50)
function sigState(t) {
  const tau = t % 60;
  const ped = tau >= 30 && tau < 46 ? 'go' : tau >= 46 && tau < 50 ? (frac(t * 2) < 0.5 ? 'go' : 'off') : 'stop';
  const car = tau < 26 || tau >= 52 ? 'g' : tau < 29 ? 'y' : 'r';
  return { ped, car };
}
const FIG_STOP = ['..##..', '..##..', '.####.', '######', '.####.', '.#..#.', '.#..#.'];
const FIG_GO = ['...##.', '...##.', '.####.', '#.###.', '..##.#', '.#..#.', '#....#'];
function renderSignal() {
  const p = pix(W, H), X = SIG.x;
  for (let y = 186; y < AB.base + 1; y++) { p.set(X, y, '#2e2c40'); p.set(X + 1, y, '#50506c'); }
  p.fill(X - 1, AB.base - 1, 4, 2, '#2a2838');
  // walker head: housing with two cells
  p.fill(X - 5, 196, 10, 18, '#1c1a26'); p.fill(X - 5, 196, 10, 1, '#3a3850'); p.fill(X - 5, 205, 10, 1, '#141220');
  for (const [cy, g] of [[198, FIG_STOP], [207, FIG_GO]]) { p.fill(X - 4, cy, 8, 7, '#0e0c16'); glyphP(p, g, X - 3, cy, '#2a2834'); }
  // car signal on an arm: horizontal, green / yellow / red
  for (let i = X + 2; i < X + 8; i++) p.set(i, 187, '#3a3850');
  p.fill(X + 7, 184, 20, 7, '#1c1a26'); p.fill(X + 7, 184, 20, 1, '#3a3850');
  for (let k = 0; k < 3; k++) p.fill(X + 9 + k * 6, 186, 4, 3, '#2a2834');
  return trim(p);
}
function drawSignal(t) {
  const s = sigState(t), X = SIG.x;
  blit(SIG_C);
  if (s.ped === 'stop') { for (const [j, row] of FIG_STOP.entries()) for (let i = 0; i < 6; i++) if (row[i] === '#') rect(ctx, X - 3 + i, 198 + j, 1, 1, '#ff4a4a'); halo(HL.sigR, X, 201, 0.9); }
  if (s.ped === 'go') { for (const [j, row] of FIG_GO.entries()) for (let i = 0; i < 6; i++) if (row[i] === '#') rect(ctx, X - 3 + i, 207 + j, 1, 1, '#4af0d0'); halo(HL.sigG, X, 210, 0.9); }
  const ci = s.car === 'g' ? 0 : s.car === 'y' ? 1 : 2, cc = ['#3ae8c0', '#ffc83a', '#ff4a3a'][ci];
  rect(ctx, X + 9 + ci * 6, 186, 4, 3, cc);
  halo(s.car === 'g' ? HL.sigG : s.car === 'y' ? HL.sigY : HL.sigR, X + 11 + ci * 6, 187, 0.7);
  return s;
}

/* ================= Pedestrians on the crossing ================= */
const WALKERS = [
  { t0: 151, t1: 169, x0: 112, x1: 392, kind: 'clear' },            // clear umbrella, left to right
  { t0: 91, t1: 108, x0: 390, x1: 108, kind: 'navy' },              // salaryman, right to left
];
function drawPerson(x, fy, t, kind, dir, mirror) {
  // designed facing right in local coordinates; dir < 0 mirrors horizontally, mirror flips below the feet
  const R = (i, j, w, h, col) => {
    ctx.fillStyle = col;
    const X = Math.round(x + (dir > 0 ? i : -i - w + 1));
    for (let k = 0; k < h; k++) ctx.fillRect(X, mirror ? 2 * fy + 1 - (fy + j + k) : fy + j + k, w, 1);
  };
  const clear = kind === 'clear', coat = clear ? '#c8a878' : '#2a2c3e', coatD = clear ? '#9a7a54' : '#1a1c2a';
  if (Math.floor(t * 4) & 1) { R(-1, -3, 1, 3, '#1a1622'); R(1, -3, 1, 3, '#1a1622'); R(-2, 0, 2, 1, '#0e0c14'); R(1, 0, 2, 1, '#0e0c14'); }
  else { R(0, -3, 1, 3, '#1a1622'); R(-1, 0, 3, 1, '#0e0c14'); }
  R(-1, -9, 3, 6, coat); R(-1, -9, 1, 6, coatD);
  if (clear) R(0, -9, 2, 1, '#d83a4a'); else { R(1, -9, 1, 2, '#e0e0ea'); R(-3, -5, 2, 2, '#4a3222'); }
  R(-1, -12, 2, 1, '#1a1420'); R(-1, -11, 1, 2, '#1a1420'); R(0, -11, 1, 2, '#e8b890');
  R(1, -8, 1, 1, '#e8b890');
  R(1, -14, 1, 6, '#9a9ab0');
  if (clear) {
    const a0 = ctx.globalAlpha;
    ctx.globalAlpha = a0 * 0.3; R(-1, -18, 5, 1, '#e8f0ff'); R(-3, -17, 9, 1, '#e8f0ff'); R(-4, -16, 11, 1, '#e8f0ff');
    ctx.globalAlpha = a0;
    R(-5, -15, 13, 1, '#d4e0ff'); R(1, -19, 1, 1, '#f4f8ff');
    for (const [i, j] of [[-2, -17], [3, -16], [-3, -16], [5, -15]]) R(i, j, 1, 1, '#ffffff');
  } else {
    R(-1, -18, 5, 1, '#2c3460'); R(-3, -17, 9, 1, '#262e58'); R(-4, -16, 11, 1, '#20284e'); R(-5, -15, 13, 1, '#4a5a94');
    R(1, -19, 1, 1, '#4a5a94');
  }
}
// small figures strolling along the far sidewalk, backlit by the arches (integer laps per loop)
const STROLLERS = [
  { k: 3, ph: 0.12, dir: 1, um: '#dfe8ff', coat: '#3a3048', clear: true }, { k: 4, ph: 0.55, dir: -1, um: '#2a3262', coat: '#2a2a3a' },
  { k: 3, ph: 0.81, dir: -1, um: '#c83a4a', coat: '#4a3a2a' }, { k: 4, ph: 0.33, dir: 1, um: '#ff7ab8', coat: '#303848' },
  { k: 3, ph: 0.47, dir: 1, um: '#1c1c28', coat: '#3a2a3a' }, { k: 7, ph: 0.2, dir: -1, bike: true, coat: '#2a3040' },
];
function drawStroller(s, x, fy, t, mirror) {
  const R = (i, j, w, h, col) => { ctx.fillStyle = col; for (let k = 0; k < h; k++) ctx.fillRect(x + i, mirror ? 2 * fy + 1 - (fy + j + k) : fy + j + k, w, 1); };
  const st = Math.floor(t * 3 + s.ph * 7) & 1;
  if (s.bike) {
    R(-3, -2, 3, 1, '#2a2a36'); R(-4, -1, 1, 1, '#2a2a36'); R(-2, -1, 1, 1, '#2a2a36'); R(-3, 0, 3, 1, '#2a2a36');
    R(2, -2, 3, 1, '#2a2a36'); R(1, -1, 1, 1, '#2a2a36'); R(3, -1, 1, 1, '#2a2a36'); R(2, 0, 3, 1, '#2a2a36');
    R(-1, -3, 3, 1, '#3a3a4a'); R(0, -7, 2, 4, s.coat); R(0, -8, 2, 1, '#1a1420');
    const fx = s.dir < 0 ? -5 : 5;
    R(fx, -3, 1, 1, '#fff6d8'); R(-fx, -3, 1, 1, '#ff3a3a');
    return;
  }
  R(st ? 0 : 1, -1, 1, 1, '#141018'); R(st ? 1 : 0, -1, 1, 1, '#221c2a');
  R(0, -5, 2, 4, s.coat); R(0, -6, 2, 1, '#1a1420'); R(1, -7, 1, 1, '#8a8aa0');
  if (s.clear) { ctx.globalAlpha *= 0.45; R(-1, -9, 3, 1, s.um); R(-2, -8, 5, 1, s.um); ctx.globalAlpha /= 0.45; R(-3, -7, 7, 1, s.um); }
  else { R(-1, -9, 3, 1, s.um); R(-2, -8, 5, 1, s.um); R(-3, -7, 7, 1, s.um); }
}
function drawStrollers(t) {
  for (const s of STROLLERS) {
    const u = frac(s.k * t / LOOP + s.ph), x = Math.round(s.dir > 0 ? lerp(-14, W + 14, u) : lerp(W + 14, -14, u));
    if (x < AB.r - 2 || x > W + 8) continue;
    ctx.globalAlpha = 0.22; drawStroller(s, x, 217, t, true);
    ctx.globalAlpha = 1; drawStroller(s, x, 217, t, false);
    if (s.bike) halo(HL.bikeLamp, x + (s.dir < 0 ? -5 : 5), 214, 0.9);
  }
}
function drawWalkers(t) {
  for (const wk of WALKERS) {
    if (t < wk.t0 || t >= wk.t1) continue;
    const s = (t - wk.t0) / (wk.t1 - wk.t0), x = Math.round(lerp(wk.x0, wk.x1, s)), dir = wk.x1 > wk.x0 ? 1 : -1;
    ctx.globalAlpha = 0.28; drawPerson(x, 239, t, wk.kind, dir, true);
    ctx.globalAlpha = 1; drawPerson(x, 239, t, wk.kind, dir, false);
  }
}

/* ================= Yokocho alley building (left foreground) ================= */
const AWIN = [];
{
  const r = rng(505), kinds = [['warm', 'toggle'], ['snack', 'tv'], ['toggle', 'blinds'], ['blinds', 'warm']];
  [48, 82, 116, 150].forEach((y, fi) => [8, 40].forEach((x, ci) =>
    AWIN.push({ id: AWIN.length, x, y, w: 24, h: 18, kind: kinds[fi][ci], n: 3 + Math.floor(r() * 5), p: 0.55, off: r() })));
}
const ACS = [{ x: 68, y: 56 }, { x: 68, y: 124 }, { x: 68, y: 158 }];
const PANELS = [
  { y: 48, style: 'tube', col: '#ff4fa8', g: [0, 1], hl: 'pink' },
  { y: 73, style: 'lit', col: '#5ee8ff', ink: '#0c2c48', g: [2, 3], hl: 'cyan' },
  { y: 98, style: 'tube', col: '#ffe060', g: [4, 5], hl: 'yellow' },
  { y: 123, style: 'lit', col: '#ff78c0', ink: '#4a0a30', g: [6, 7], hl: 'pink' },
  { y: 148, style: 'tube', col: '#5ee8ff', g: [1, 3], hl: 'cyan' },
];
const PANEL_H = 23;
const LANTERNS = [{ x: 12, big: false }, { x: 29, big: false }, { x: 59, big: false }, { x: 91, big: true }];
const VEND = { x: 62, y: 208, w: 22, h: 36 };

function renderPanel(pn, on) {
  const w = COL.w, h = PANEL_H, p = pix(w, h), c = hex(pn.col);
  const dimOf = k => [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k)];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const edge = i === 0 || y === 0 || i === w - 1 || y === h - 1;
    if (edge) { p.set(i, y, '#120c1a'); continue; }
    if (pn.style === 'lit') {
      const v = 1 - Math.abs(i - w / 2) / w * 0.6 - Math.abs(y - h / 2) / h * 0.4;
      p.set(i, y, on ? (v - 0.55 > bayer(i, y) * 0.4 ? mixRGB(c, [255, 255, 255], 0.35) : c) : dimOf(0.22));
    } else p.set(i, y, '#1a1024');
  }
  pn.g.forEach((gi, k) => {
    const gx = 4, gy = 3 + k * 9, g = G7[gi];
    if (pn.style === 'lit') { glyphP(p, g, gx, gy, on ? pn.ink : dimOf(0.12)); return; }
    if (on) for (let j = -1; j <= 7; j++) for (let i = -1; i <= 7; i++) {        // tube glow halo in the box
      const near = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]].some(([a, b]) => g[j + b] && g[j + b][i + a] === '#');
      if (near && !(g[j] && g[j][i] === '#')) p.set(gx + i, gy + j, dimOf(0.35));
    }
    glyphP(p, g, gx, gy, on ? mixRGB(c, [255, 255, 255], 0.45) : dimOf(0.2));
  });
  return p.done();
}
function mixRGB(a, b, t) { return [Math.round(lerp(a[0], b[0], t)), Math.round(lerp(a[1], b[1], t)), Math.round(lerp(a[2], b[2], t))]; }
function renderBand(on) {                              // horizontal backlit sign over the ramen shop
  const w = 88, h = 10, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const edge = i === 0 || y === 0 || i === w - 1 || y === h - 1;
    p.set(i, y, edge ? '#1a0c10' : on ? (y === 1 ? '#ff6a74' : pick(['#c81e30', '#e02a3a'], 0.8 - y / h * 0.6, i, y)) : '#3a1016');
  }
  // bowl emblem + glyphs
  const ink = on ? '#fff4ec' : '#5a2a2c';
  for (let i = 3; i <= 9; i++) p.set(i, 5, ink); for (let i = 4; i <= 8; i++) p.set(i, 6, ink); p.set(5, 7, ink); p.set(6, 7, ink); p.set(7, 7, ink);
  p.set(8, 2, ink); p.set(9, 3, ink); p.set(10, 2, ink); p.set(11, 3, ink);
  [5, 2, 7, 0, 4].forEach((gi, k) => glyphP(p, G5[gi], 16 + k * 14, 3, ink));
  return p.done();
}
function renderSideSign(on) {                           // narrow violet tube sign on the left edge
  const w = 7, h = 58, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.set(i, y, i === 0 || y === 0 || i === w - 1 || y === h - 1 ? '#120c1a' : '#180e22');
  [3, 6, 1, 4].forEach((gi, k) => glyphP(p, G5[gi], 1, 3 + k * 14, on ? '#e6a8ff' : '#3a2448'));
  return p.done();
}
function renderLantern(big) {
  const w = big ? 11 : 9, h = big ? 15 : 12, p = pix(w, h), c = (w - 1) / 2;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const body = y >= 1 && y <= h - 3, rows = h - 3;
    if (y === 0 || y === h - 2) { if (Math.abs(i - c) <= (big ? 3 : 2)) p.set(i, y, '#1a0c0c'); continue; }
    if (y === h - 1) { if (i === c) p.set(i, y, '#c83a2a'); continue; }
    if (!body) continue;
    const yy = (y - 0.5) / rows * 2 - 1, half = c * Math.sqrt(Math.max(0, 1 - yy * yy * 0.55)) + 0.5;
    const dx = Math.abs(i - c);
    if (dx > half) continue;
    const lv = 1 - dx / (half + 0.5) * 0.9 - Math.abs(yy) * 0.25 - (y % 2 === 0 ? 0.08 : 0);
    p.set(i, y, pick(['#6a1414', '#a82020', '#e03c2a', '#ff7040', '#ffb070'], lv, i, y));
  }
  const g = big ? ['###', '.#.', '###', '#.#', '###'] : ['###', '.#.', '###'];
  g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '#') p.set(c - 1 + i, (big ? 5 : 4) + j, '#2a0a08'); });
  return p.done();
}
function renderNoren() {                               // four indigo panels with a white mark (drawn per panel)
  const pw = 5, h = 14, list = [];
  for (let k = 0; k < 4; k++) {
    const p = pix(pw, h);
    for (let y = 0; y < h; y++) for (let i = 0; i < pw; i++) p.set(i, y, y === 0 ? '#34427c' : (i === 0 ? '#18224c' : '#1e2a5c'));
    const gx = k * 6;                                  // global x of this panel within the 23 px noren
    for (let y = 3; y < 11; y++) for (let i = 0; i < pw; i++) {
      const X = gx + i, d = Math.hypot(X - 11, y - 6.5);
      if (Math.abs(d - 4) < 0.7 || (k === 0 && i === 2 && y > 4 && y < 10) || (k === 3 && (y === 5 || y === 8) && i > 0)) p.set(i, y, '#eae4f4');
    }
    list.push(p.done());
  }
  return list;
}
function renderCounterFront() {                        // stock pots + inner counter, in front of the chef
  const p = pix(24, 12);
  for (const [x0, x1] of [[2, 8], [13, 20]]) for (let y = 3; y < 10; y++) for (let i = x0; i <= x1; i++)
    p.set(i, y, y === 3 ? '#f0e8f0' : i === x0 ? '#e0d8e0' : i === x1 ? '#6a6070' : y === 4 ? '#5a5060' : '#b0a8b4');
  for (let i = 0; i < 24; i++) { p.set(i, 10, '#a86a40'); p.set(i, 11, '#6a3a22'); }
  return p.done();
}

function renderAlley() {
  const p = pix(W, H), r = rng(606);
  // rooftop: water tank, antenna, the back of a rooftop billboard frame
  for (let y = 20; y <= 31; y++) for (let i = 12; i <= 30; i++) p.set(i, y, i === 14 ? '#5a5478' : i === 30 ? '#221e36' : (y === 24 || y === 28) ? '#2e2a44' : y === 20 ? '#4a4468' : '#3a3452');
  for (const lx of [13, 21, 29]) for (let y = 32; y < 38; y++) p.set(lx, y, '#2a2640');
  for (let k = 0; k < 8; k++) { p.set(13 + k, 32 + (k % 6), '#221e34'); }
  for (let y = 8; y < 38; y++) p.set(60, y, '#2e2a44');
  for (let i = 57; i <= 63; i++) p.set(i, 12, '#2e2a44'); for (let i = 58; i <= 62; i++) p.set(i, 18, '#2e2a44');
  for (let y = 20; y <= 37; y++) for (let i = 68; i <= 100; i++) {
    const fr = y === 20 || y === 31 || i === 68 || i === 100 || (y < 31 && Math.abs((i - 68) - (y - 20) * 2.9) < 1.2) || (y < 31 && Math.abs((100 - i) - (y - 20) * 2.9) < 1.2);
    const leg = y > 31 && (i === 72 || i === 96 || i === 84);
    if (fr || leg) p.set(i, y, y === 20 ? '#2e2848' : '#1c1830');
  }
  // parapet and tiled facade
  [['#4e4668', 38], ['#3a3454', 39], ['#2a2440', 40], ['#1e1a30', 41]].forEach(([c, y]) => { for (let i = 0; i < AB.r; i++) p.set(i, y, c); });
  for (let y = 42; y < 184; y++) for (let i = 0; i < AB.r; i++) {
    let col = (y % 4 === 1 || i % 3 === 0) ? '#26203a' : (hash(i >> 1, y >> 2, 3) < 0.07 ? '#332c4a' : '#2c2640');
    if (i < 2) col = '#1c182c'; else if (i >= AB.r - 2) col = '#3a3452';
    if (y === 77 || y === 111 || y === 145) col = '#3a3454'; else if (y === 78 || y === 112 || y === 146) col = '#1c1828';
    p.set(i, y, col);
  }
  // windows: aluminium frames + dark glass (lit states are drawn per frame)
  for (const w of AWIN) {
    p.fill(w.x - 1, w.y - 1, w.w + 2, w.h + 2, '#56526e');
    for (let y = w.y; y < w.y + w.h; y++) for (let i = w.x; i < w.x + w.w; i++) p.set(i, y, (i - y) % 9 === 0 ? '#1c1a32' : '#110e20');
    p.fill(w.x + (w.w >> 1), w.y, 1, w.h, '#3a3650');
    p.fill(w.x - 2, w.y + w.h + 1, w.w + 4, 1, '#4a4664');
  }
  // outdoor AC units + small lit box sign on floor 2
  for (const a of ACS) {
    p.fill(a.x, a.y, 15, 9, '#4a4a62'); p.fill(a.x, a.y, 15, 1, '#6c6c88'); p.fill(a.x, a.y + 8, 15, 1, '#2a2a3c');
    for (let y = a.y + 1; y < a.y + 8; y++) for (let i = a.x + 1; i < a.x + 8; i++) { const d = Math.hypot(i - a.x - 4, y - a.y - 4); if (d < 3.4) p.set(i, y, d > 2.4 ? '#2a2a3a' : ((i + y) & 1 ? '#3a3a4e' : '#22222e')); }
    for (let y = a.y + 2; y < a.y + 8; y += 2) p.fill(a.x + 9, y, 5, 1, '#34344a');
    p.set(a.x + 1, a.y + 9, '#2a2a3a'); p.set(a.x + 13, a.y + 9, '#2a2a3a');
  }
  // drain pipe
  for (let y = 40; y < AB.base; y++) { p.set(88, y, '#3a3450'); p.set(89, y, y % 16 === 0 ? '#5a5478' : '#2a2640'); }
  // sign column frame
  for (let y = 44; y < 173; y++) { p.set(COL.x + 1, y, '#1a1424'); p.set(COL.x + COL.w - 2, y, '#1a1424'); }
  for (const y of [60, 110, 160]) for (let i = 86; i < COL.x + 2; i++) p.set(i, y, '#2a2238');
  // eave over the ground floor
  for (let i = 0; i < AB.r; i++) { p.set(i, 184, '#4a3a4c'); p.set(i, 185, '#2e2230'); p.set(i, 186, '#241a26'); p.set(i, 187, '#1c141e'); if (i % 3 !== 1) p.set(i, 188, '#241a26'); }
  // ground floor wall: wooden slats (left), plaster (right)
  for (let y = 189; y < AB.base; y++) for (let i = 0; i < AB.r; i++) {
    let col = i < 58 ? (i % 4 === 0 ? '#22161a' : '#2e2024') : ((y % 5 === 0 || i % 6 === 0) ? '#25202e' : '#2c2636');
    if (y < 192) col = '#1a1216';
    p.set(i, y, col);
  }
  // exhaust vent (steam source)
  p.fill(15, 194, 10, 4, '#5a5670'); p.fill(15, 194, 10, 1, '#8a86a0'); for (let i = 16; i < 24; i += 2) p.set(i, 196, '#2a2838');
  // ramen counter window: warm interior with a menu strip and a shelf of bowls
  p.fill(4, 202, 25, 23, '#3a2418');
  for (let y = 203; y < 224; y++) for (let i = 5; i < 28; i++) p.set(i, y, pick(['#e08a48', '#f0a860', '#ffc680', '#ffdc9c'], 1 - (y - 203) / 21 * 0.8, i, y));
  for (let i = 6; i < 27; i += 3) { p.set(i, 204, '#fff6e6'); p.set(i, 205, '#fff6e6'); p.set(i, 206, (i * 7) % 5 ? '#fff6e6' : '#c8a080'); }
  for (let i = 6; i < 27; i++) p.set(i, 210, '#8a5030');
  for (let i = 7; i < 26; i += 4) { p.set(i, 209, '#fff4e8'); p.set(i + 1, 209, '#e8dcd0'); }
  p.fill(2, 224, 29, 1, '#b8784a'); p.fill(2, 225, 29, 2, '#6a3a22');
  for (const sx of [8, 20]) { p.fill(sx, 232, 5, 2, '#b42a34'); p.fill(sx, 232, 5, 1, '#e24a52'); p.fill(sx + 2, 234, 1, 9, '#6a6482'); p.fill(sx + 1, 243, 3, 1, '#4a4660'); p.set(sx + 3, 234, '#8a84a4'); }
  // entrance: lattice sliding door with warm light and a waiting customer's shadow
  p.fill(32, 196, 23, 48, '#3a2418');
  for (let y = 198; y < AB.base; y++) for (let i = 34; i < 53; i++) {
    let col = pick(['#c8743a', '#e89452', '#f8b670'], 0.9 - (y - 198) / 46 * 0.5, i, y);
    if ((i - 34) % 3 === 2 || y === 220 || y === 221) col = '#5a3018';
    if (i >= 41 && i <= 45 && y > 214 && y < 238 && (i - 34) % 3 !== 2 && y !== 220 && y !== 221) col = '#7a4020';
    p.set(i, y, col);
  }
  p.fill(32, 196, 23, 2, '#2a1810');
  // vending machine (static body; glow is additive per frame)
  const V = VEND;
  for (let y = V.y; y < V.y + V.h; y++) for (let i = V.x; i < V.x + V.w; i++) {
    let col = i === V.x || i === V.x + V.w - 1 || y === V.y ? '#3a4054' : i === V.x + 1 ? '#f4f8ff' : i >= V.x + V.w - 3 ? '#9aa4ba' : '#dfe6f2';
    if (y >= V.y + 1 && y <= V.y + 3 && i > V.x && i < V.x + V.w - 1) col = y === V.y + 2 ? '#ffffff' : '#8ad8ff';
    if (y >= V.y + 5 && y <= V.y + 23 && i >= V.x + 2 && i <= V.x + V.w - 4) col = '#eaf6ff';
    p.set(i, y, col);
  }
  const CANS = ['#e8404a', '#3a6ae8', '#3ab86a', '#ff9a2a', '#8a5a3a', '#f4f4f4', '#f0d040', '#8a4ad0', '#2ac8d8'];
  for (let row = 0; row < 3; row++) {
    const cy = V.y + 6 + row * 6;
    for (let k = 0; k < 6; k++) {
      const cx = V.x + 3 + k * 3, col = CANS[Math.floor(r() * CANS.length)];
      p.fill(cx, cy, 2, 4, col); p.set(cx, cy, '#ffffff');
      p.set(cx, cy + 5, k % 2 ? '#ff5050' : '#40e080');
    }
  }
  p.fill(V.x + 2, V.y + 25, V.w - 5, 1, '#9aa4ba');
  p.fill(V.x + 15, V.y + 27, 3, 4, '#3a4054'); p.set(V.x + 16, V.y + 28, '#80ff80');
  p.fill(V.x + 3, V.y + 30, 10, 3, '#1a1e2a');
  // beer crates
  for (let y = 230; y < AB.base; y++) for (let i = 86; i < 101; i++) {
    const top = y < 237;
    let col = top ? (y === 230 ? '#d84a4a' : '#a42a30') : (y === 237 ? '#f0c040' : '#c89a28');
    if ((y === 233 || y === 240) && (i - 86) % 5 > 0 && (i - 86) % 5 < 4) col = top ? '#5a1418' : '#6a4a10';
    p.set(i, y, col);
  }
  for (let i = 87; i < 100; i += 2) p.set(i, 229, '#c8c8d0');
  return trim(p);
}

/* ================= Cherry tree in full bloom (right foreground) + street lamp ================= */
const BRANCHES = [
  [448, 254, 444, 204, 6, 4.5],
  [444, 206, 424, 178, 3.5, 2.5], [424, 178, 402, 160, 2.5, 1.8], [402, 160, 384, 172, 1.8, 1],
  [444, 206, 440, 162, 3, 2.2], [440, 162, 430, 126, 2.2, 1.4], [430, 126, 414, 104, 1.4, 1],
  [444, 206, 462, 172, 3, 2], [462, 172, 470, 130, 2, 1.4], [470, 130, 462, 98, 1.4, 1],
  [462, 172, 480, 160, 2, 1.5], [440, 162, 456, 136, 1.5, 1], [424, 178, 420, 150, 1.5, 1],
];
const BLOOM = [
  [380, 171, 10, 6], [404, 144, 14, 9], [418, 128, 16, 12], [421, 110, 15, 11], [412, 97, 11, 8],
  [437, 131, 18, 14], [453, 108, 18, 13], [467, 93, 14, 10], [474, 124, 14, 14], [480, 155, 12, 15],
  [459, 150, 16, 12], [434, 160, 15, 10], [468, 180, 12, 9], [446, 92, 12, 8], [428, 96, 12, 8],
  [486, 106, 10, 12], [394, 158, 9, 6],
];
const BLOSSOM = ['#3a0f36', '#5e1a50', '#8a2a6c', '#b84488', '#e2689e', '#ff94bc', '#ffc4d8', '#ffe8f0'];
const crownV = (x, y) => { let v = -9; for (const [cx, cy, rx, ry] of BLOOM) v = Math.max(v, 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2); return v; };
function renderTree() {
  const p = pix(W, H), glints = [], r = rng(313);
  for (const [x0, y0, x1, y1, w0, w1] of BRANCHES) {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 2);
    for (let s = 0; s <= n; s++) {
      const u = s / n, x = lerp(x0, x1, u), y = lerp(y0, y1, u), rw = lerp(w0, w1, u) / 2;
      for (let j = Math.floor(y - rw); j <= Math.ceil(y + rw); j++) for (let i = Math.floor(x - rw); i <= Math.ceil(x + rw); i++) {
        if (Math.hypot(i - x, j - y) > rw + 0.3) continue;
        const lit = i - x < -rw * 0.35;
        p.set(i, j, lit ? '#5a3446' : (hash(i, j, 4) < 0.3 ? '#2a1824' : '#1c1019'));
      }
    }
  }
  // dark inner mass so gaps between clusters read as shadowed depth, not holes
  for (let y = 76; y < 200; y++) for (let x = 360; x < W; x++) {
    const v = crownV(x, y) + (vnoise(x / 6, y / 6, 8) - 0.5) * 0.6;
    if (v > 0.4 && hash(x, y, 9) < 0.92) p.set(x, y, v > 0.7 ? '#2a0c2a' : '#34102f');
  }
  // blossom clusters: small balls shaded towards the lamp (near) or the sky (far), back to front
  const balls = [];
  for (let k = 0; k < 8000 && balls.length < 430; k++) {
    const x = 362 + r() * 128, y = 76 + r() * 124, v = crownV(x, y);
    if (v < -0.08 + r() * 0.1) continue;
    balls.push({ x, y, r: 1.6 + r() * 2.4 * (0.55 + 0.45 * clamp(v + 0.4, 0, 1)), tint: (r() - 0.5) * 0.14 });
  }
  balls.sort((a, b) => a.y - b.y);
  for (const b of balls) {
    const lx = LAMP.x - b.x, ly = LAMP.y - b.y, ld = Math.max(1, Math.hypot(lx, ly));
    const lampK = Math.pow(clamp(1 - ld / 90, 0, 1), 1.15);
    const dx0 = lerp(-0.45, lx / ld, lampK), dy0 = lerp(-0.9, ly / ld, lampK), dn = Math.hypot(dx0, dy0) || 1;
    const base = 0.35 + lampK * 0.4 + clamp((140 - b.y) / 70, 0, 1) * 0.08 + b.tint;
    for (let j = Math.floor(b.y - b.r); j <= Math.ceil(b.y + b.r); j++) for (let i = Math.floor(b.x - b.r); i <= Math.ceil(b.x + b.r); i++) {
      const dx = (i - b.x) / b.r, dy = (j - b.y) / b.r, dd = dx * dx + dy * dy;
      if (dd > 1 || (dd > 0.62 && hash(i, j, 77) < 0.38)) continue;
      const nd = (dx * dx0 + dy * dy0) / dn;
      const floret = hash(i, j, 79) < 0.07 ? 0.22 : 0;
      const L = Math.min(0.95, base + nd * 0.3 + (nd > 0.45 && dd > 0.3 ? 0.1 : 0) - (nd < -0.5 ? 0.1 : 0) - dd * 0.06 + (hash(i, j, 78) - 0.5) * 0.12 + floret);
      p.set(i, j, pick(BLOSSOM, L, i, j));
      if (L > 0.74 && hash(i, j, 21) < 0.22) glints.push([i, j]);
    }
  }
  // loose florets around the edge of the crown
  for (let k = 0; k < 220; k++) {
    const x = 360 + r() * 130, y = 74 + r() * 128, v = crownV(x, y);
    if (v < -0.45 || v > 0.05) continue;
    const ld = Math.hypot(x - LAMP.x, y - LAMP.y);
    p.set(x, y, pick(BLOSSOM, 0.42 + clamp(1 - ld / 90, 0, 1) * 0.4 + r() * 0.15, Math.round(x), Math.round(y)));
  }
  // trunk bark below the crown
  for (let y = 190; y < 254; y++) { const x = 448 - Math.round((254 - y) * 0.08); p.set(x - 3, y, '#6a3c52'); if (y % 5 === 0) p.set(x, y, '#2e1c28'); }
  return { c: trim(p), glints };
}
function renderLamp() {
  const p = pix(W, H), X = LAMP.post, hy = LAMP.y - 4;         // head rows hy..hy+2, glowing underside at hy+2
  for (let y = hy - 2; y < 252; y++) { p.set(X, y, '#1e1e2c'); p.set(X + 1, y, (y & 7) === 0 ? '#5a5a74' : '#3a3a52'); }
  p.fill(X - 1, 247, 4, 5, '#262636'); p.fill(X - 1, 247, 4, 1, '#44445c');
  for (let i = LAMP.x + 2; i <= X + 1; i++) p.set(i, hy - 2, i === X + 1 ? '#5a5a74' : '#3a3a52');
  p.set(LAMP.x + 1, hy - 1, '#3a3a52');
  for (let i = LAMP.x - 5; i <= LAMP.x + 5; i++) {
    const e = Math.abs(i - LAMP.x);
    p.set(i, hy, e === 5 ? '#34344a' : '#4a4a64'); p.set(i, hy + 1, '#1e1e2c');
    if (e <= 4) p.set(i, hy + 2, e === 4 ? '#c8c0d0' : '#fffaf2');
  }
  return trim(p);
}
/** additive light cone under the lamp (soft beam in the rain) */
function renderCone() {
  const top = LAMP.y - 2, bot = 252, p = pix(W, H), c = hex('#ffe6f0');
  for (let y = top; y < bot; y++) {
    const f = (y - top) / (bot - top), hw = 4 + f * 40;
    for (let x = Math.floor(LAMP.x - hw); x <= Math.ceil(LAMP.x + hw); x++) {
      const e = Math.abs(x - LAMP.x) / hw;
      const a = Math.pow(1 - e, 1.6) * Math.pow(1 - f, 1.3) * 0.22;
      const q = Math.floor(a * 40 + bayer(x, y)) / 40;
      if (q > 0) p.set(x, y, c, Math.round(q * 255));
    }
  }
  return trim(p);
}
/** flattened dithered ellipse of light (for pools of light on the wet ground) */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}

/* ================= Sakura petals (3 depths; flutter, rotate, flip) ================= */
const PETAL_SHAPES = [null, ['##.', '###'], ['.###.', '#####', '#####', '.#.#.']];
function makePetal(size, white, back) {
  const g = PETAL_SHAPES[size], h = g.length, w = g[0].length, p = pix(w, h);
  const pal = back ? (white ? ['#ffffff', '#f6e8ee', '#e2c6d2'] : ['#ffeef4', '#ffcade', '#ec9cbc'])
    : (white ? ['#fff6fa', '#ffe2ee', '#f4bcd0'] : ['#ffe2ee', '#ffa8c8', '#ee6c9e']);
  g.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '#') p.set(i, j, j === 0 ? pal[0] : j === h - 1 ? pal[2] : pal[1]); });
  return p.done();
}
const PETALS = [[], [], []];
{
  const r = rng(909);
  [
    { count: 34, nMin: 7, nMax: 10, amp: 3, drift: [90, 170], fall: [170, 215], tree: 0 },
    { count: 28, nMin: 9, nMax: 13, amp: 5, drift: [190, 320], fall: [100, 160], tree: 0.65 },
    { count: 18, nMin: 11, nMax: 15, amp: 8, drift: [280, 440], fall: [110, 170], tree: 0.7 },
  ].forEach((d, size) => {
    for (let i = 0; i < d.count; i++) PETALS[size].push({
      id: size * 100 + i, size, n: d.nMin + Math.floor(r() * (d.nMax - d.nMin + 1)), ph: r(),
      ks: 40 + Math.floor(r() * 40), p2: r(), kr: 20 + Math.floor(r() * 30), p3: r(), kf: 30 + Math.floor(r() * 50), p4: r(),
      amp: d.amp * (0.7 + r() * 0.6), drift: lerp(d.drift[0], d.drift[1], r()), fall: lerp(d.fall[0], d.fall[1], r()),
      tree: d.tree, white: r() < 0.3,
    });
  });
}
function drawPetals(list, t) {
  for (const pt of list) {
    const q = t * pt.n / LOOP + pt.ph, fq = Math.floor(q), cyc = fq % pt.n, u = q - fq;
    let x0, y0, fall = pt.fall;
    if (hash(pt.id, cyc, 3) < pt.tree) { x0 = 382 + hash(pt.id, cyc, 1) * 100; y0 = 96 + hash(pt.id, cyc, 2) * 90; }
    else { x0 = 30 + hash(pt.id, cyc, 1) * (W + 60); y0 = -6 - hash(pt.id, cyc, 2) * 16; if (pt.size > 0) fall += 110; }
    const sw = wave(t, pt.ks, pt.p2);
    const x = x0 - u * pt.drift + sw * pt.amp, y = y0 + u * fall + (1 - Math.abs(sw)) * (pt.size + 1) * 0.8;
    if (x < -8 || x > W + 8 || y > 268) continue;
    const a = Math.min(1, u / 0.06, (1 - u) / 0.1);
    const fl = Math.cos(TAU * (pt.kf * t / LOOP + pt.p4)), back = fl < 0;
    if (pt.size === 0) {
      ctx.globalAlpha = a * 0.85; ctx.fillStyle = back ? '#ffd8e8' : '#f59cc2';
      ctx.fillRect(Math.round(x), Math.round(y), Math.abs(fl) > 0.45 ? 2 : 1, 1);
      continue;
    }
    const spr = PETAL_SPR[pt.size][pt.white ? 1 : 0][back ? 1 : 0], w = spr.width, h = spr.height;
    const rot = sw * 0.9 + 0.5 * wave(t, pt.kr, pt.p3);
    ctx.save();
    ctx.globalAlpha = a;
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(rot); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
    ctx.drawImage(spr, -(w >> 1), -(h >> 1));
    if (!back && fl > 0.88 && pt.size === 2) {                 // catches the light when facing us
      ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a * (fl - 0.88) / 0.12 * 0.32;
      ctx.drawImage(PETAL_SHINE, -(w >> 1), -(h >> 1));
    }
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= Rain (lighter than Paris), tinted by nearby lights ================= */
const RAIN_LIGHTS = [
  { x: 101, y: 110, r: 36, c: [255, 128, 196] }, { x: 44, y: 184, r: 30, c: [255, 120, 100] },
  { x: 73, y: 224, r: 34, c: [200, 236, 255] }, { x: LAMP.x, y: LAMP.y + 30, r: 58, c: [255, 232, 244] },
  { x: TWR.cx, y: 118, r: 38, c: [255, 170, 100] },
  { x: 173, y: 152, r: 20, c: [255, 150, 214], board: false },   // the slide sign, which shines without the billboard (whose soft face lights no rain)
].map(l => ({ ...l, r2: l.r * l.r }));
function litAt(x, y, board) {
  for (let i = 0; i < RAIN_LIGHTS.length; i++) {
    const l = RAIN_LIGHTS[i], dx = x - l.x, dy = y - l.y;
    if ((l.board === undefined || l.board === board) && dx * dx + dy * dy < l.r2) return i;
  }
  return -1;
}
function nearLand(x, a, b) {
  if (x >= 0 && x < AB.r) {
    if (a < 0.3) return 184;
    if (x >= VEND.x && x < VEND.x + VEND.w && a < 0.6) return VEND.y;
    return AB.base + 2 + Math.floor(b * 22);
  }
  return 246 + Math.floor(b * 22);
}
const RAIN = [
  { count: 130, nMin: 150, nMax: 178, fall: 230, ff: 1, len: 2, slope: -0.12, a: 0.34, splash: '#9aa2e0', land: (x, a) => 180 + Math.floor(a * 14) },
  { count: 84, nMin: 190, nMax: 215, fall: 290, ff: 0.88, len: 4, slope: -0.18, a: 0.44, splash: '#a8b0ea', land: (x, a) => GROUND + 4 + Math.floor(a * (H - GROUND - 8)) },
  { count: 40, nMin: 250, nMax: 282, fall: 330, ff: 0.8, len: 7, slope: -0.26, a: 0.55, splash: '#c4ccf6', land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(100 + li);
  L.col = `rgba(160,168,232,${L.a})`;
  L.lit = RAIN_LIGHTS.map(l => rgbaOf(l.c, Math.min(0.9, L.a + 0.2)));
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
// a picked weather (site/js/engine/weather.js): Tokyo draws its own rain and petals (the petals only with its own weather);
// snow and leaves land behind the viaduct, on the street and past the bottom of the frame, and catch the rain's lights
const WEATHER = weatherPlans(['rain', 'petals']);
const WX_LAND = [[180, 194], [GROUND + 4, H - 4], [H + 4, H + 4]];
const wxLight = l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: '#' + l.c.map(v => v.toString(16).padStart(2, '0')).join('') });
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.filter(l => l.board !== true).map(wxLight) };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS.filter(l => l.board !== false).map(wxLight) };
function drawRain(L, t, board) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -12 + hash(d.id, cyc, 11) * (W + 24 - L.fall * L.slope * 0.5);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      const li = litAt(xh, yh, board);
      ctx.fillStyle = li < 0 ? L.col : L.lit[li];
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh + k * L.slope), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 5;
      const hgt = Math.sin(Math.PI * s) * (big ? 3 : 1.5), spread = 1 + s * (big ? 3 : 1.5);
      const li = litAt(xl, yl, board);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = li < 0 ? L.splash : L.lit[li];
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1);
      ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 40; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 40), ph: r() }); }
function drawRings(t, streaks) {
  for (const g of RINGS) {
    const q = t * g.n / LOOP + g.ph, fq = Math.floor(q), cyc = fq % g.n, u = q - fq;
    if (u > 0.45) continue;
    const s = u / 0.45;
    let x, y;
    if (hash(g.id, cyc, 3) < 0.4) {                                  // many land in the puddles
      const [cx, cy, rx, ry] = PUDDLES[Math.floor(hash(g.id, cyc, 4) * PUDDLES.length)];
      x = cx + (hash(g.id, cyc, 1) - 0.5) * rx * 1.4; y = cy + (hash(g.id, cyc, 2) - 0.5) * ry * 1.2;
    } else { x = hash(g.id, cyc, 1) * W; y = 220 + hash(g.id, cyc, 2) * 46; }
    if (x < 116 && y < AB.base + 2) continue;
    const rr = 1 + s * 4.5, ry = rr * 0.34, pts = 6 + Math.floor(rr * 2.5);
    const st = streaks.find(k => Math.abs(k.x - x) < k.w + 2);
    ctx.fillStyle = st ? st.col : '#8e96d8';
    ctx.globalAlpha = (1 - s) * 0.55;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= Ramen steam and dripping AC units ================= */
const STEAM = [];
{ const r = rng(61); for (let i = 0; i < 24; i++) STEAM.push({ n: 44 + Math.floor(r() * 12), ph: r(), k: 16 + Math.floor(r() * 20), p: r(), x: 16 + r() * 7 }); }
function drawSteam(t) {
  for (const s of STEAM) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = s.x - u * 16 + wave(t, s.k, s.p) * 3 * u, y = 194 - u * 58 - u * u * 6;
    const rr = Math.min(6, 1 + Math.floor(u * 7));
    ctx.globalAlpha = 0.55 * Math.min(1, u * 8) * Math.pow(1 - u, 1.4);
    ctx.drawImage(PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  for (let k = 0; k < 6; k++) {                                     // wisps from the pots inside
    const q = t * (60 + k * 7) / LOOP + k * 0.21, u = q - Math.floor(q);
    const x = (k % 2 ? 22 : 10) + wave(t, 30 + k * 3, k * 0.1) * 1.5, y = 215 - u * 11;
    ctx.globalAlpha = 0.32 * Math.sin(Math.PI * u);
    const rr = 1 + (k % 2);
    ctx.drawImage(PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
const landY = x => x >= 2 && x <= 30 ? 224 : x >= VEND.x && x < VEND.x + VEND.w ? VEND.y : x >= 86 && x < 101 ? 229 : AB.base + 2;
const DRIPS = [
  { x: ACS[0].x + 1, y0: ACS[0].y + 10, y1: ACS[1].y, n: 50, ph: 0.1 },
  { x: ACS[1].x + 1, y0: ACS[1].y + 10, y1: ACS[2].y, n: 59, ph: 0.47 },
  { x: ACS[2].x + 1, y0: ACS[2].y + 10, y1: 184, n: 66, ph: 0.73 },
];
for (let k = 0; k < 8; k++) { const x = 5 + k * 12 + (k % 3); DRIPS.push({ x, y0: 189, y1: landY(x), n: 70 + k * 7, ph: k * 0.23 }); }
function drawDrips(t) {
  const g = 420;
  ctx.fillStyle = '#b8c2ff';
  for (const d of DRIPS) {
    const T = LOOP / d.n, q = t / T + d.ph, u = q - Math.floor(q), tau = u * T, rel = T * 0.55;
    if (tau < rel) { ctx.globalAlpha = 0.2 + 0.6 * tau / rel; ctx.fillRect(d.x, d.y0, 1, 1); continue; }
    const s = tau - rel, y = d.y0 + 0.5 * g * s * s;
    if (y < d.y1 - 1) { ctx.globalAlpha = 0.85; ctx.fillRect(d.x, Math.round(y), 1, 2); continue; }
    const ss = (s - Math.sqrt(2 * (d.y1 - 1 - d.y0) / g)) / 0.3;
    if (ss >= 1) continue;
    const hy = d.y1 - 1 - Math.round(Math.sin(Math.PI * ss) * 2), sp = 1 + Math.round(ss * 2);
    ctx.globalAlpha = 1 - ss;
    ctx.fillRect(d.x - sp, hy, 1, 1); ctx.fillRect(d.x + sp, hy, 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Timed events (loop seconds) ================= */
const TRAIN_IN = 20, TRAIN_DUR = 12.5;                 // passes at t = 20 (left to right) and t = 140 (right to left)
const PANEL_FLICK = [[[88, 89.6]], [], [[37, 39.4], [158.2, 160]], [[121, 122.2]], [[201, 204.6]]];
const BAND_FLICK = [[64, 65.4], [177, 178.2]];
const SIDE_FLICK = [[12, 13.5], [110, 112.8], [226, 227.4]];
const VEND_FLICK = [[141.3, 142.6]];
const LAMP_FLICK = [[72.5, 73.9], [196, 197.2]];
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop
const TVC = ['#3c6cff', '#6d9cff', '#a8ccff', '#2a44b8', '#e2efff', '#5a7ad8'];

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FUJI_C, SKYT_C, FAR, HR, TOWER, NEAR, NEAR_BARE, BBAD, BB_PITCH, SLIDE_C, CHASE_C, MAST_C,
  VIA_C, TRAIN, STREET_C, SIG_C, ALLEY_C, PANEL_C, BAND_C, SIDE_C, LANT_C, NOREN_C, COUNTER_C, TREE, LAMP_C, CONE_C, POOL,
  VPOOL, PETAL_SPR, PETAL_SHINE, PUFF_C, VIG_C, STREAKS, STREAKS_BARE, UFO_C, BIRD_C, BOZU_C;
/** a sprite from rows of letters and their colours ('.' clear), mirrored when `flip` */
function spriteOf(rows, key, flip = false) {
  const w = rows[0].length, p = pix(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) { const col = key[row[flip ? w - 1 - i : i]]; if (col) p.set(i, j, col); } });
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(34, '#9a8cff', 0.2, 1.8), moonCore: makeHalo(12, '#f4e8ff', 0.28, 1.5),
    tower: makeHalo(86, '#ff6a2a', 0.15, 1.6), towerMid: makeHalo(44, '#ffa060', 0.15, 1.6),
    deck: makeHalo(20, '#ffd8a0', 0.28), topdeck: makeHalo(12, '#e0f0ff', 0.3),
    st: makeHalo(18, '#7ab8ff', 0.16), strobe: makeHalo(4, '#e8f0ff', 0.6, 1), band: makeHalo(14, '#b080ff', 0.4), red: makeHalo(5, '#ff3030', 0.6, 1),
    pink: makeHalo(28, '#ff4fa8', 0.3), cyan: makeHalo(26, '#40e0ff', 0.28), yellow: makeHalo(24, '#ffd040', 0.26),
    violet: makeHalo(18, '#c070ff', 0.3), sign: makeHalo(30, '#ff3a3a', 0.2),
    pinkS: makeHalo(10, '#ff5ab4', 0.3), cyanS: makeHalo(10, '#5ee8ff', 0.3), violetS: makeHalo(10, '#c070ff', 0.32), yellowS: makeHalo(10, '#ffe060', 0.28),
    lantern: makeHalo(12, '#ff5a30', 0.4), lanternBig: makeHalo(34, '#ff6a3a', 0.14),
    vend: makeHalo(40, '#c0e4ff', 0.26), vendCore: makeHalo(14, '#f0f8ff', 0.3), shop: makeHalo(22, '#ffb060', 0.24),
    arch: makeHalo(18, '#ffa050', 0.22), plamp: makeHalo(9, '#ffd090', 0.4), archBar: makeHalo(18, '#ff60d0', 0.22),
    win: makeHalo(12, '#ffb45a', 0.14), tv: makeHalo(13, '#5a8cff', 0.3), snack: makeHalo(14, '#ff6ac0', 0.3),
    lamp: makeHalo(64, '#ffd6e8', 0.17, 1.7), lampCore: makeHalo(14, '#fff8f0', 0.42, 1.4),
    sigR: makeHalo(8, '#ff3a3a', 0.45), sigG: makeHalo(8, '#30ffd0', 0.45), sigY: makeHalo(8, '#ffc83a', 0.45),
    spark: makeHalo(13, '#b8d8ff', 0.55, 1.2), bikeLamp: makeHalo(7, '#fff0c8', 0.45), head: makeHalo(22, '#fff4d0', 0.5), trainWin: makeHalo(18, '#ffeec0', 0.14),
    bbGlow: makeHalo(24, '#8fb8d8', 0.07), chaser: makeHalo(16, '#ff4a3a', 0.26),
    slide: SLIDES.map(s => makeHalo(24, s.glow, 0.28)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FUJI_C = renderFuji(); SKYT_C = renderSkytree(); FAR = renderFarCity(); HR = renderHighrises();
  TOWER = renderTower(); NEAR = renderNearCity(true); NEAR_BARE = renderNearCity(false);
  BBAD = { back: renderBBBack(), ...renderBBTop() };
  BB_PITCH = makePitch(BB.w, BB.h, { accent: '#ff5fa2' });
  SLIDE_C = SLIDES.map(renderSlide); CHASE_C = [renderChaser(false), renderChaser(true)];
  MAST_C = renderMasts(); VIA_C = renderViaduct(); TRAIN = renderTrain();
  STREET_C = renderStreet(); SIG_C = renderSignal();
  ALLEY_C = renderAlley(); PANEL_C = PANELS.map(pn => [renderPanel(pn, false), renderPanel(pn, true)]);
  BAND_C = [renderBand(false), renderBand(true)]; SIDE_C = [renderSideSign(false), renderSideSign(true)];
  LANT_C = [renderLantern(false), renderLantern(true)]; NOREN_C = renderNoren(); COUNTER_C = renderCounterFront();
  TREE = renderTree(); LAMP_C = renderLamp(); CONE_C = renderCone();
  UFO_C = spriteOf(UFO_ROWS, UFO_KEY); BOZU_C = spriteOf(BOZU_ROWS, BOZU_KEY); BIRD_C = [spriteOf(BIRD_ROWS, BIRD_KEY), spriteOf(BIRD_ROWS, BIRD_KEY, true)];
  POOL = makePool(42, 7, '#ffe0ee', 0.32); VPOOL = makePool(24, 5, '#c0e4ff', 0.32);
  PETAL_SPR = [null, 1, 2].map(s => s && [0, 1].map(wh => [makePetal(s, wh, false), makePetal(s, wh, true)]));
  { const [c, x] = makeCanvas(5, 4); rect(x, 0, 0, 5, 4, '#ffffff'); PETAL_SHINE = c; }
  PUFF_C = [];
  for (let pr = 1; pr <= 6; pr++) {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
      const d = Math.hypot(i - pr, y - pr) / pr;
      if (d < 1 && (1 - d) * 1.7 > bayer(i, y) * 0.9) p.set(i, y, y < pr * 0.8 ? '#f4eef8' : '#c8c0dc');
    }
    PUFF_C[pr] = p.done();
  }
  VIG_C = renderVignette();
  const streaks = board => [
    ...BAYS.filter(b => b.kind === 'izakaya' || b.kind === 'ramen').map(b => ({ x: b.x + (b.w >> 1), w: 8, col: '#ffb060', a: 0.34, seed: b.id })),
    ...BAYS.filter(b => b.kind === 'bar').map(b => ({ x: b.x + (b.w >> 1), w: 6, col: '#ff70d0', a: 0.3, seed: 20 + b.id })),
    { x: TWR.cx, w: 6, col: '#ff8a40', a: 0.32, seed: 40 },
    board ? { x: BB.x + (BB.w >> 1), w: 5, col: '#a8d8d4', a: 0.12, seed: 41 } : { x: 173, w: 5, col: '#ff7ac0', a: 0.22, seed: 41 },
    { x: 277, w: 4, col: '#ff5a4a', a: 0.22, seed: 42 },
  ];
  STREAKS = streaks(true); STREAKS_BARE = streaks(false);
}
function renderVignette() {
  const p = pix(W, H), c = [6, 3, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}

/* ================= Per-frame: sky and far city ================= */
function drawScroll(cv, y, k, t) { const off = Math.floor(frac(k * t / LOOP) * W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6e2ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  drawUfo(t);
  blit(FUJI_C);
}
/* hidden things: the saucer in the sky, the paper doll in a window, the white-eye in the cherry tree */
const UFO_ROWS = ['....ccc....', '..ttttttt..', 'hhlhhlhhlhh', '..uuuuuuu..'];
const UFO_KEY = { c: '#8fc8dc', t: '#8286ae', h: '#666a94', l: '#ffe6a0', u: '#3e4168' };
function drawUfo(t) {
  const w = UFO_UP.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const tau = t - w[0], a = smooth(0, 1.5, tau) * (1 - smooth(w[1] - w[0] - 1.5, w[1] - w[0], tau));
  const x = UFO.x + Math.round(wave(t, 16, 0.1) * 1.4), y = UFO.y + Math.round(wave(t, 40, 0.3) * 0.9);
  ctx.globalAlpha = a;
  ctx.drawImage(UFO_C, x, y);
  ctx.globalAlpha = a * 0.3;                             // a breath of light under it
  rect(ctx, x + 3, y + 4, 5, 1, '#9af0e0'); rect(ctx, x + 4, y + 5, 3, 1, '#9af0e0');
  ctx.globalAlpha = 1;
}
function drawBozu(t) {
  const sw = wave(t, 24, 0.3) * 0.7 + wave(t, 61, 0.1) * 0.4, { x, y } = BOZU;   // it turns a little on its string
  const at = j => x + Math.round(sw * j / 12);
  ctx.fillStyle = '#6a4a3a';
  ctx.fillRect(x, y, 1, 6); ctx.fillRect(at(6), y + 6, 1, 1);   // the string hangs straight, its end follows the doll
  ctx.drawImage(BOZU_C, 0, 0, 5, 4, at(8) - 2, y + 7, 5, 4);     // its head and the ribbon round its neck
  ctx.drawImage(BOZU_C, 0, 4, 5, 2, at(11) - 2, y + 11, 5, 2);   // its skirt swings a little further
}
const BOZU_ROWS = ['.wws.', '.kwk.', '.wws.', '.rrr.', '.www.', 'wwwws'];
const BOZU_KEY = { w: '#f8f4f6', s: '#d6ccd8', k: '#3a2c3a', r: '#d2586a' };
const BIRD_ROWS = ['..gg...', 'kgwgg..', '.yggggt', '..gdd.t', '.bbbbb.'];   // on its twig
const BIRD_KEY = { g: '#7f9840', d: '#566e30', w: '#f2f4e8', y: '#cabe58', k: '#2c2420', t: '#425426', b: '#5a3446' };
function drawBird(t) {
  const flip = hash(7, step(t, 16), 5) < 0.4 ? 1 : 0;   // it turns round on its twig every so often
  ctx.drawImage(BIRD_C[flip], BIRD.x, BIRD.y);
}
const PLANE = { t0: 48, t1: 124, x0: W + 12, x1: -14, y0: 47, y1: 39 };
function drawPlane(t) {                                // a jet far away: dim body, nav lights, strobe
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
  rect(ctx, x - 2, y, 6, 1, '#2a2450'); rect(ctx, x, y - 1, 2, 1, '#2a2450'); rect(ctx, x + 3, y - 1, 1, 1, '#2a2450');
  rect(ctx, x - 2, y, 1, 1, '#ff4a4a'); rect(ctx, x + 3, y + 1, 1, 1, '#4aff8a');
  const f = frac(t);
  if (f < 0.08 || (f > 0.18 && f < 0.24)) { rect(ctx, x + 1, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x + 1, y - 1, 0.9); }
}
function drawSkytree(t) {
  halo(HL.st, SKYT.x, 113, 0.9); halo(HL.st, SKYT.x, 90, 0.7);
  blit(SKYT_C);
  const by = lerp(172, 56, 0.5 - 0.5 * Math.cos(TAU * 6 * t / LOOP));   // 6 up-and-down trips per loop
  ctx.fillStyle = mix('#72d2ff', '#b67cff', 0.5 + 0.5 * wave(t, 3));
  for (let y = Math.max(SKYT.top, Math.floor(by - 9)); y <= Math.min(174, Math.ceil(by + 9)); y++) {
    const d = Math.abs(y - by) / 9;
    if (d >= 1) continue;
    const w = stW(y), x0 = SKYT.x - ((w - 1) >> 1);
    for (let i = 0; i < w; i++) if (1 - d > bayer(x0 + i, y) * 0.9) ctx.fillRect(x0 + i, y, 1, 1);
  }
  halo(HL.band, SKYT.x, by, 0.7);
}
function drawCityLights(t) {
  blit(FAR.c);
  for (const w of FAR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) rect(ctx, w.x, w.y, 1, 1, w.col);
}
function drawHighrises(t) {
  blit(HR.c);
  let rx = 0, ry = 0, rw = 0;                         // runs of adjacent lit floors are copied in one go
  for (const s of HR.segs) {
    if (hash(s.id + 7, Math.floor(frac(t / LOOP + s.off) * s.n), 4) >= s.p) continue;
    if (rw && s.y === ry && s.x === rx + rw) { rw += s.w; continue; }
    if (rw) ctx.drawImage(HR.lit, rx, ry, rw, 1, rx, ry, rw, 1);
    rx = s.x; ry = s.y; rw = s.w;
  }
  if (rw) ctx.drawImage(HR.lit, rx, ry, rw, 1, rx, ry, rw, 1);
}
function drawTower(t) {
  const cx = TWR.cx, br = 0.92 + 0.08 * wave(t, 8);
  halo(HL.tower, cx, 146, br); halo(HL.towerMid, cx, 104, br);
  ctx.drawImage(TOWER.c, TOWER.ox, TOWER.oy);
  halo(HL.deck, cx, TWR.deck + 4, 0.85); halo(HL.topdeck, cx, TWR.top + 2, 0.8);
  for (let k = 0; k < 4; k++) {                                   // visitors strolling on the main deck
    const u = frac((3 + k) * t / LOOP + k * 0.27), x = cx - 9 + Math.floor((1 - Math.abs(2 * u - 1)) * 18);
    rect(ctx, x, k < 2 ? TWR.deck + 2 : TWR.deck + 5, 1, 2, '#7a4430');
  }
}
function drawNear(t, pitch, board) {
  const near = board ? NEAR : NEAR_BARE;
  blit(near.c);
  for (const w of near.wins) if (hash(w.id + 90, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p) rect(ctx, w.x, w.y, 1, 2, w.col);
  for (const s of near.signs) {
    let lv = 0.9 + 0.1 * wave(t, 1800 + s.id * 50, s.id * 0.3);
    if (s.id === 1 && frac(t / 40) > 0.9 && hash(70, Math.floor(t * 16), 2) < 0.5) { lv = 0; rect(ctx, s.x + 1, s.y + 1, 2, s.h - 2, '#241a30'); }
    halo(HL[s.hl + 'S'], s.x + 2, s.y + (s.h >> 1), lv);
  }
  if (board) drawBillboard(t, pitch); else drawSlides(t);
  // small glyph sign with a chasing bulb border
  const c = BILLBOARDS[0], on = !(frac(t / 20) > 0.93 && frac(t * 3) < 0.5);
  ctx.drawImage(CHASE_C[on ? 1 : 0], c.x, c.y);
  const ch = Math.floor(t * 8);
  ctx.fillStyle = '#ffd890';
  for (let i = 0; i < c.w; i += 2) {
    if (((i >> 1) + ch) % 3 === 0) { ctx.fillRect(c.x + i, c.y - 1, 1, 1); ctx.fillRect(c.x + c.w - 1 - i, c.y + c.h, 1, 1); }
  }
  if (on) halo(HL.chaser, c.x + c.w / 2, c.y + c.h / 2, 0.85);
}
function drawBlinkers(t, board) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  const all = [...HR.blinks, ...FAR.masts, ...(board ? NEAR : NEAR_BARE).blinks, [SKYT.x, SKYT.top - 1], [60, 7],
    [TWR.cx, TWR.ant - 1], [TWR.cx - 5, TWR.top - 2], [TWR.cx + 5, TWR.top - 2], [TWR.cx - 11, TWR.deck - 2], [TWR.cx + 11, TWR.deck - 2]];
  for (const [x, y] of all) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
}

/* ================= Per-frame: train, viaduct, arches ================= */
function trainAt(t) {
  const tau = t % 120;
  if (tau < TRAIN_IN || tau >= TRAIN_IN + TRAIN_DUR) return null;
  const s = (tau - TRAIN_IN) / TRAIN_DUR, dir = t < 120 ? 1 : -1;
  const x = Math.round(dir > 0 ? lerp(-TLEN - 30, W + 30, s) : lerp(W + 30, -TLEN - 30, s));
  return { x, dir };
}
function drawTrain(t) {
  const tr = trainAt(t);
  if (!tr) return;
  ctx.drawImage(tr.dir > 0 ? TRAIN.right : TRAIN.left, tr.x, TRAIN_Y);
  const headX = tr.dir > 0 ? tr.x + TLEN : tr.x;
  // headlight beam along the viaduct
  const g = ctx.createLinearGradient(headX, 0, headX + tr.dir * 90, 0);
  g.addColorStop(0, 'rgba(255,244,210,0.35)'); g.addColorStop(1, 'rgba(255,244,210,0)');
  ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = g;
  ctx.beginPath(); ctx.moveTo(headX, 180); ctx.lineTo(headX + tr.dir * 90, 172); ctx.lineTo(headX + tr.dir * 90, 186); ctx.lineTo(headX, 182); ctx.closePath(); ctx.fill();
  ctx.globalCompositeOperation = 'source-over';
  halo(HL.head, headX, 181, 0.9);
  for (let k = 20; k < TLEN; k += 40) halo(HL.trainWin, tr.x + k, 176, 0.9);
  // pantograph sparks now and then
  for (const cI of [1, 3]) {
    const px0 = tr.dir > 0 ? tr.x + cI * (CAR + GAP) + 30 : tr.x + TLEN - 1 - (cI * (CAR + GAP) + 30);
    const hv = hash(cI, step(t, 1440), 17);
    if (hv < 0.06) {
      halo(HL.spark, px0, 163, 1 - hv / 0.06 * 0.5);
      ctx.fillStyle = '#eef6ff'; ctx.fillRect(px0, 162, 1, 2);
      ctx.fillStyle = '#9ac8ff'; ctx.fillRect(px0 - 1 - Math.round(hv * 30), 164 + Math.round(hv * 20), 1, 1);
    }
  }
}
function drawArches(t) {
  PLAMPS.forEach((x, i) => halo(HL.plamp, x, 182, 0.8 + 0.2 * wave(t, 500 + i * 13, i * 0.2)));
  for (const b of BAYS) {
    if (b.kind === 'shutter' || b.kind === 'dark') continue;
    let f = 0.85 + 0.1 * wave(t, 300 + b.id * 7, b.id * 0.13);
    if (b.id === 4 && hash(b.id, Math.floor(t * 12), 9) < 0.08) f *= 0.6;
    halo(b.kind === 'bar' ? HL.archBar : HL.arch, b.x + (b.w >> 1), 208, f);
  }
}

/* ================= Per-frame: wet street ================= */
const PUDDLES_FAR = PUDDLES.filter(q => q[0] > 116), PUDDLES_NEAR = PUDDLES.filter(q => q[0] < 116);
function drawStreet(t, tr, streaks, rain) {
  blit(STREET_C);
  reflectRows(t, { top: GROUND, rows: H - GROUND, x: AB.r, w: W - AB.r, alpha: 0.5, puddles: PUDDLES_FAR });
  rect(ctx, AB.r, GROUND, W - AB.r, H - GROUND, 'rgba(10,6,28,0.22)');
  for (const s of streaks) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, GROUND + 1, H);
  if (tr) for (let k = 24; k < TLEN; k += 30) {
    const x = tr.x + k;
    if (x > AB.r && x < W) streak(ctx, t, x, 6, '#ffe6b0', 0.22, k, GROUND + 18, H);
  }
  if (rain) drawRings(t, streaks);
}

/* ================= Per-frame: the alley building ================= */
function drawWindow(w, t) {
  const x = w.x, y = w.y, ww = w.w, hh = w.h;
  if (w.kind === 'tv') {
    const s = step(t, 1200), I = 0.45 + 0.55 * hash(13, s);
    rect(ctx, x, y, ww, hh, '#0a0e22');
    ctx.globalAlpha = I * 0.75; rect(ctx, x, y, ww, hh, TVC[Math.floor(hash(12, s) * TVC.length)]); ctx.globalAlpha = 1;
    rect(ctx, x + (ww >> 1), y, 1, hh, '#10142c');
    halo(HL.tv, x + ww / 2, y + hh / 2, I);
    return;
  }
  if (w.kind === 'snack') {
    rect(ctx, x, y, ww, hh, '#e04a9a'); rect(ctx, x, y, ww, 6, '#ff7ac0');
    ctx.fillStyle = '#8a1a5a'; for (let i = x + 1; i < x + ww; i += 3) ctx.fillRect(i, y + 7, 1, hh - 8);   // bead curtain
    ctx.fillStyle = '#fff0fa'; for (const [i, j] of [[3, 2], [4, 2], [5, 2], [4, 3], [3, 4], [5, 4], [4, 5]]) ctx.fillRect(x + 15 + i, y + j, 1, 1);
    rect(ctx, x + (ww >> 1), y, 1, hh, '#6a1244');
    halo(HL.snack, x + ww / 2, y + hh / 2, 0.9 + 0.1 * wave(t, 900));
    return;
  }
  let I = 1;
  if (w.kind === 'toggle') I = hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p ? 1 : 0;
  if (!I) return;
  if (w.kind === 'blinds') {
    rect(ctx, x, y, ww, hh, '#ffe0a4');
    ctx.fillStyle = '#b0783c'; for (let j = y + 1; j < y + hh; j += 2) ctx.fillRect(x, j, ww, 1);
  } else {
    rect(ctx, x, y, ww, 6, '#ffdc9c'); rect(ctx, x, y + 6, ww, hh - 6, '#f6b462');
    rect(ctx, x, y, 3, hh, '#c8783c'); rect(ctx, x + ww - 3, y, 3, hh, '#c8783c');
  }
  rect(ctx, x + (ww >> 1), y, 1, hh, '#6a4a3a');
  halo(HL.win, x + ww / 2, y + hh / 2, 0.8);
}
function signLevel(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 24), 3);
  return hv > 0.55 ? 1 : hv > 0.3 ? 0.4 : 0;
}
function drawSprite(on, off, x, y, lv) {
  if (lv >= 1) { ctx.drawImage(on, x, y); return; }
  ctx.drawImage(off, x, y);
  if (lv > 0) { ctx.globalAlpha = lv; ctx.drawImage(on, x, y); ctx.globalAlpha = 1; }
}
function drawAlley(t, rain) {
  blit(ALLEY_C);
  for (const w of AWIN) drawWindow(w, t);
  drawBozu(t);
  // stacked vertical neon column: steady hum, a panel flickers now and then
  const hum = 0.93 + 0.07 * wave(t, 2400);
  PANELS.forEach((pn, k) => {
    const lv = signLevel(t, PANEL_FLICK[k], 30 + k);
    drawSprite(PANEL_C[k][1], PANEL_C[k][0], COL.x, pn.y, lv);
    halo(HL[pn.hl], COL.x + 7, pn.y + 11, lv * hum);
  });
  const sl = signLevel(t, SIDE_FLICK, 40);
  drawSprite(SIDE_C[1], SIDE_C[0], 1, 88, sl); halo(HL.violet, 4, 117, sl * hum);
  const bl = signLevel(t, BAND_FLICK, 41);
  drawSprite(BAND_C[1], BAND_C[0], 3, 174, bl); halo(HL.sign, 46, 179, bl * hum);
  // chef behind the counter (sways between the pots), pots and counter in front
  const cx = 15 + Math.round(wave(t, 20, 0.2) * 2 + wave(t, 53, 0.6));
  rect(ctx, cx, 207, 4, 2, '#f4f0f0'); rect(ctx, cx, 209, 4, 2, '#3a2218'); rect(ctx, cx - 1, 211, 6, 12, '#2a1810');
  rect(ctx, cx, 208, 4, 1, '#d02a2a');
  ctx.drawImage(COUNTER_C, 4, 212);
  halo(HL.shop, 16, 214, 0.9);
  // noren: four panels swaying a little in the wind
  const wind = wave(t, 24, 0.1) * 0.8 + wave(t, 61, 0.4) * 0.5;
  NOREN_C.forEach((c, k) => {
    for (let j = 0; j < 14; j += 5) {
      const off = Math.round(wind * (0.4 + k * 0.15) * (j / 13) * 1.8);
      ctx.drawImage(c, 0, j, 5, Math.min(5, 14 - j), 32 + k * 6 + off, 198 + j, 5, Math.min(5, 14 - j));
    }
  });
  halo(HL.shop, 43, 222, 0.8);
  // red paper lanterns: sway from their hooks, candle-like glow
  LANTERNS.forEach((ln, k) => {
    const spr = LANT_C[ln.big ? 1 : 0], w = spr.width, h = spr.height;
    const sw = wave(t, 36 + k * 5, k * 0.23) * 1.3 + wind * 0.8;
    rect(ctx, ln.x, 189, 1, 1, '#1a0c0c');
    for (let j = 0; j < h; j++) ctx.drawImage(spr, 0, j, w, 1, ln.x - (w >> 1) + Math.round(sw * (j + 1) / h), 190 + j, w, 1);
    const fl = 0.85 + 0.1 * wave(t, 1211, k * 0.3) + 0.05 * wave(t, 2333, k * 0.7);
    const lx = ln.x + Math.round(sw * 0.5), ly = 190 + (h >> 1);
    halo(HL.lantern, lx, ly, fl);
    if (ln.big) halo(HL.lanternBig, lx, ly, fl);
  });
  // vending machine glow (a tube stutters once in a while)
  const vl = inEvent(t, VEND_FLICK) ? (hash(51, Math.floor(t * 20), 1) > 0.4 ? 1 : 0.35) : 1;
  if (vl < 1) { ctx.globalAlpha = 1 - vl; rect(ctx, VEND.x + 2, VEND.y + 5, VEND.w - 5, 19, '#3a4a66'); ctx.globalAlpha = 1; }
  halo(HL.vend, VEND.x + 10, VEND.y + 14, vl); halo(HL.vendCore, VEND.x + 10, VEND.y + 14, vl);
  drawPool(VPOOL, VEND.x + 10, AB.base + 3, 0.8 * vl);
  // a cat loafing on top of the vending machine: tail swings, eyes blink
  const CAT = ['.#.#......', '.####.....', '######....', '#########.', '##########'];
  ctx.fillStyle = '#0c0a14';
  CAT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') ctx.fillRect(VEND.x + 3 + i, VEND.y - 5 + j, 1, 1); });
  rect(ctx, VEND.x + 5, VEND.y - 1, 7, 1, '#3a4a6a');
  const ts = wave(t, 30, 0.2) * 0.9 + wave(t, 97, 0.5) * 0.4;
  rect(ctx, VEND.x + 13, VEND.y - 1, 1, 1, '#0c0a14');
  for (let j = 0; j < 6; j++) ctx.fillRect(VEND.x + 13 + Math.round(ts * j / 5), VEND.y + j, 1, 1);
  if (!(frac(t / 4) > 0.955 || frac(t / 30) > 0.985)) { rect(ctx, VEND.x + 4, VEND.y - 3, 1, 1, '#d6f060'); }
  drawSteam(t);
  if (rain) drawDrips(t);
  // the wet sidewalk in front mirrors the shop front
  reflectRows(t, { top: AB.base, rows: H - AB.base, x: 0, w: 118, alpha: 0.42, squash: 1.3, puddles: PUDDLES_NEAR });
  streak(ctx, t, COL.x + 7, 4, '#ff6ab4', 0.42 * hum, 61, AB.base + 1, H);
  streak(ctx, t, VEND.x + 10, 10, '#d0eeff', 0.3 * vl, 62, AB.base + 1, H);
  streak(ctx, t, 44, 5, '#ff6a4a', 0.3, 63, AB.base + 1, H);
}

/* ================= Per-frame: cherry tree + lamp ================= */
function lampLevel(t) {
  let v = 0.96 + 0.04 * wave(t, 1500, 0.3);
  if (inEvent(t, LAMP_FLICK)) { const hv = hash(11, Math.floor(t * 20)); v *= hv > 0.45 ? 1 : hv > 0.2 ? 0.5 : 0.15; }
  return v;
}
function drawTree(t) {
  const li = lampLevel(t);
  blit(TREE.c);
  drawBird(t);
  const gs = step(t, 1440);
  ctx.fillStyle = '#fff6fa';
  TREE.glints.forEach(([x, y], i) => { if (hash(i, gs, 31) < 0.03) ctx.fillRect(x, y, 1, 1); });
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.6 * li; blit(CONE_C);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  blit(LAMP_C);
  if (li < 0.9) rect(ctx, LAMP.x - 4, LAMP.y - 2, 9, 1, `rgba(40,30,40,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y - 1, li);
  drawPool(POOL, LAMP.x + 2, 252, li);
  // a moth circling the lamp
  const a = TAU * 40 * t / LOOP;
  rect(ctx, Math.round(LAMP.x + Math.cos(a) * 6), Math.round(LAMP.y + 3 + Math.sin(2 * a) * 2.5), 1, 1, '#fff2e0');
  return li;
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own billboard ad; render(t, { pitch: true }) paints the site's pitch on its face;
 *  render(t, { billboard: false }) is the skyline from before the billboard, with the slide sign (the loop videos);
 *  render(t, { weather }) draws a WEATHERS pick (site/js/engine/weather.js), the city's own when left out */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawSkytree(t);
  drawCityLights(t);
  if (wx.petals) drawPetals(PETALS[0], t);
  if (wx.rain) drawRain(RAIN[0], t, billboard);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawHighrises(t);
  drawTower(t);
  drawNear(t, pitch, billboard);
  drawBlinkers(t, billboard);
  blit(MAST_C);
  drawTrain(t);
  blit(VIA_C);
  drawArches(t);
  const tr = trainAt(t);
  drawStreet(t, tr, billboard ? STREAKS : STREAKS_BARE, wx.rain);
  drawStrollers(t);
  drawSignal(t);
  drawWalkers(t);
  if (wx.petals) drawPetals(PETALS[1], t);
  if (wx.rain) drawRain(RAIN[1], t, billboard);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawAlley(t, wx.rain);
  drawTree(t);
  if (wx.rain) drawRain(RAIN[2], t, billboard);
  if (wx.petals) drawPetals(PETALS[2], t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'tokyo',
  name: 'Tokyo',
  tagline: 'Neon rain by Tokyo Tower',
  timeZone: 'Asia/Tokyo',
  accent: '#ff5fa2',
  focusX: TWR.cx,
  landmark: [TWR.cx - 19, TWR.cx + 20],                // Tokyo Tower's legs
  landmarkTop: TWR.ant,                                // the row of the antenna's tip, which landscape crops keep
  billboard: BB,
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: TWR.cx - 15, y: TWR.ant, w: 31, h: 144 },                     // Tokyo Tower, down to the viaduct
    { kind: 'neon', x: BILLBOARDS[0].x, y: BILLBOARDS[0].y, w: BILLBOARDS[0].w, h: BILLBOARDS[0].h },   // the red rooftop sign beside it
    { kind: 'neon', x: COL.x, y: PANELS[0].y, w: COL.w, h: PANELS[4].y + PANEL_H - PANELS[0].y },       // the alley's column of signs
    { kind: 'cat', x: VEND.x + 2, y: VEND.y - 7, w: 13, h: 6 },                          // loafing on the vending machine
  ],
  secrets: [                                           // hidden things a tap finds, each a passport sticker (site/js/ui/secrets.js)
    { id: 'bozu', name: 'a teru teru bozu against the rain', x: BOZU.x - 3, y: BOZU.y, w: 7, h: 13, art: [
      '.....N......', '.....N......', '....www.....', '...wwwwW....', '...wkwkW....', '...wwwwW....',
      '....rrr.....', '...wwwwW....', '..wwwwwwW...', '..wwwwwwW...', '.wwwwwwwwW..', '.w.ww.ww.W..'] },
    { id: 'bird', name: 'a white-eye in the cherry tree', x: BIRD.x - 1, y: BIRD.y - 1, w: 9, h: 7, art: [
      '..p......p..', '.....gggg...', '....gggggg..', '..kkgwkgggg.', '....yggggGG.', '....yyggGGGG',
      '.....yggGG.G', '......gGG..G', 'NnNNnNNNnNN.', '..p..n...p..', '.ppp.....pp.', '..p......p..'] },
    { id: 'ufo', name: 'a flying saucer', x: UFO.x - 2, y: UFO.y - 2, w: 15, h: 9, at: UFO_UP, art: [
      '............', '....bbbb....', '...bwbbbb...', '...bbbbbb...', '.SssssssssS.', 'SsysysysysSS',
      'SSSSSSSSSSSS', '..KKKKKKKK..', '....cccc....', '...c....c...', '..c......c..', '............'] },
  ],
  ambience: { rain: 0.5, traffic: 0.3, crowd: 0.15, wind: 0.1 },
  events: [
    { t: TRAIN_IN, type: 'train' }, { t: 30, type: 'chime' }, { t: 90, type: 'chime' },
    { t: 120 + TRAIN_IN, type: 'train' }, { t: 150, type: 'chime' }, { t: 210, type: 'chime' },
  ],
  create(c) {
    ctx = c;
    [RBUF, RCTX] = makeCanvas(W, 100);
    prerender();
    return { render: renderFrame };
  },
};
